/*
 * Carbisdale - Main Entry Point
 *
 * Orchestrator-Modul: Initialisiert die Anwendung, verbindet alle Module
 * und steuert die Render-Loop.
 *
 * Event-Handling und Logik sind in separate Module extrahiert:
 * - appContext.js — Zentraler State (Singleton-Referenzen, Flags)
 * - persistence.js — Settings/Content laden/speichern (localStorage)
 * - clipboard.js — Copy/Cut/Paste mit Rich-Text
 * - contextMenuActions.js — Rechtsklick-Menü und Formatierung
 * - mouseHandlers.js — Alle Maus-Events
 * - keyboardHandlers.js — Alle Keyboard-Events, Zoom, Resize
 */

import { Screen } from './screen.js'
import { Cursor } from './cursor.js'
import { Renderer } from './renderer.js'
import { Overlay } from './overlay.js'
import { Menu } from './menu.js'
import { LayoutManager } from './layout.js'
import { ProseEditor } from './editor/ProseEditor.js'
import { MetadataManager } from './MetadataManager.js'
import { FileManager } from './FileManager.js'
import { DialogManager } from './DialogManager.js'
import { SearchManager } from './SearchManager.js'
import { PaneId, DisplayMode, DISPLAY_MODES } from './config.js'
import { SoundManager } from './SoundManager.js'
import { CoreConsole } from './core/CoreConsole.js'
import { AgentService } from './agent/AgentService.js'
import { buildCursorContext } from './agent/cursorContext.js'
import { buildCursorContextForMock } from './agent/mockCursorContext.js'
import { MockAgentTransport } from './agent/MockAgentTransport.js'
import { isMockEnabled } from './core/ConsoleCommands.js'
import { ReviewController } from './editor/ReviewController.js'
import { AutoCorrect } from './AutoCorrect.js'
import { DictationManager, DictationState } from './dictation/DictationManager.js'
import { Position } from './editor/EditorModel.js'
import { ContextMenu } from './ContextMenu.js'
import { ctx } from './appContext.js'
import { loadSettings, saveSettings, _flushSettings, applySettings, applyLayoutSettings, saveContent, saveContentImmediately, saveEditorState, restoreCursorFromProjectState } from './persistence.js'
import { IndexedDBStore } from './persistence/IndexedDBStore.js'
import { ProjectManager } from './persistence/ProjectManager.js'
import { SyncService } from './persistence/SyncService.js'
import { createRemoteChangesHandler } from './persistence/remoteChangesHandler.js'
import { replaceUntouchedUntitledWithCloudProject } from './persistence/firstSyncAdoption.js'
import { FolderManager } from './persistence/FolderManager.js'
import { TabManager } from './TabManager.js'
import { SpellCheckManager } from './spellcheck/SpellCheckManager.js'
import { DictionaryLoader } from './spellcheck/DictionaryLoader.js'
import { paragraphToCheckInput } from './spellcheck/paragraphAdapter.js'
import { SpellCheckMenu } from './spellcheck/SpellCheckMenu.js'
import { TabBarRenderer } from './TabBarRenderer.js'
import { ProjectDashboard } from './dashboard/ProjectDashboard.js'
import { toggleCloudForProject, enableCloudForNewProject } from './dashboard/cloudToggleLegacy.js'
import { createTabGuard } from './persistence/tabGuard.js'
import { AuthService } from './auth/AuthService.js'
import { LoginPage, showBlockedPage } from './auth/LoginPage.js'
import { UserMenu } from './UserMenu.js'
import { MemoryEditor } from './memory/MemoryEditor.js'
import { PersonalDictEditor } from './spellcheck/PersonalDictEditor.js'
import { dispatchContextAction, generateProjectStatisticsMenuItems } from './contextMenuActions.js'
import { handleMouseDown, handleMouseUp, handleMouseWheel, handleClick, handleMouseMove, handleContextMenu, getScrollbarPane, getActiveScrollSource, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel } from './mouseHandlers.js'
import { initTouchKeyboardSink, installBeforeInputHandler } from './touchKeyboard.js'
import { handleResize, handleKeydown, handleKeyup, handleFullscreenChange, handleDimensionChange, handleZoom, toggleFullscreen, effectiveEditorBounds, calcOutlinePaneWidth, calcContextPaneWidth } from './keyboardHandlers.js'
import { scrollProfiler } from './ScrollProfiler.js'

/**
 * Mappt eine Primary-Sprache auf die ihr "natuerliche" Fallback-Sprache.
 * Spec §6.4: Default ist `EN als Fallback fuer DE` und umgekehrt — Hauptfall
 * sind eingestreute Fremdwoerter ("deploy", "Worker", "ContextMenu") in einem
 * deutschen Text und vice versa. `null`/`undefined` bedeutet: Fallback aus.
 *
 * @param {string|null} primary - Primary-Sprachcode oder null wenn Spellcheck
 *        deaktiviert ist.
 * @param {boolean} enabled - User-Toggle fuer Fallback. False schaltet die
 *        Fallback-Sprache komplett ab, egal welche Primary-Sprache aktiv ist.
 * @returns {string|null}
 */
function _resolveSpellcheckFallback(primary, enabled) {
    if (!primary || !enabled) return null
    if (primary === 'de') return 'en'
    if (primary === 'en') return 'de'
    return null
}

/**
 * Oeffnet eine URL in einem wiederverwendbaren Tab. Wenn `existingWin` noch
 * lebt (nicht .closed) und same-origin steuerbar ist, wird dort hinnavigiert
 * und der Tab fokussiert. Andernfalls wird ein neuer Tab geoeffnet. Liefert
 * die (neue oder bestehende) Window-Reference zurueck.
 *
 * Hintergrund: window.open(url, name) wird wegen Tab-Isolation/COOP in
 * modernen Browsern nicht mehr zuverlaessig recycelt — der Name-Lookup
 * funktioniert nur innerhalb desselben Browsing-Contexts. Das Halten der
 * Reference im Editor-Tab ist die robuste Variante fuer same-origin Ziele.
 *
 * @param {Window|null} existingWin
 * @param {string} url
 * @returns {Window|null}
 */
function _reuseOrOpenTab(existingWin, url) {
    if (existingWin && !existingWin.closed) {
        try {
            existingWin.location.href = url
            existingWin.focus()
            return existingWin
        } catch {
            // Cross-origin (User hat anderswo hinnavigiert) o.ae.: neuer Tab.
        }
    }
    return window.open(url, '_blank')
}

/**
 * Serialisierte PUT-Chain fuer das Personal-Dict. Schnelle "Add to personal
 * dictionary"-Klicks im SpellCheckMenu wuerden sonst zwei PUTs parallel
 * abfeuern; der zweite kann beim Server vor dem ersten ankommen und das
 * erste Wort verschwinden lassen (Spellcheck-Review 2026-05-10 Finding C3b).
 *
 * Jeder Aufruf wartet auf den vorigen — der Body wird beim Senden frisch
 * ausgelesen, also bekommt der Server immer den dann aktuellen Stand.
 */
let _personalSyncChain = Promise.resolve()
function schedulePersonalSync() {
    _personalSyncChain = _personalSyncChain.then(() => {
        if (!ctx.spellCheckManager) return
        if (!ctx.authService || !ctx.authService.isLoggedIn || ctx.authService.isOffline) return
        return fetch('/api/user/spellcheck', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ personal: ctx.spellCheckManager.getPersonal() })
        }).catch(err => console.warn('[Spellcheck] Personal-Sync fehlgeschlagen:', err && err.message))
    })
    return _personalSyncChain
}

/**
 * Pro-Projekt-Ignore-Sync: pro Projekt-ID eine eigene serialisierte Chain.
 * Beim Project-Switch landet ein noch laufender Write auf dem alten Projekt,
 * neue Writes laufen auf der Chain des neuen Projekts.
 */
const _projectIgnoreSyncChains = new Map()
function scheduleProjectIgnoreSync(projectId) {
    if (!projectId) return Promise.resolve()
    const previous = _projectIgnoreSyncChains.get(projectId) || Promise.resolve()
    const next = previous.then(async () => {
        if (!ctx.projectManager || !ctx.spellCheckManager) return
        try {
            await ctx.projectManager.setProjectSpellcheckIgnore(
                projectId,
                ctx.spellCheckManager.getProjectIgnore()
            )
        } catch (err) {
            console.warn('[Spellcheck] setProjectSpellcheckIgnore fehlgeschlagen:', err && err.message)
        }
    })
    _projectIgnoreSyncChains.set(projectId, next)
    return next
}

/**
 * Sweept alle Editor-Paragraphen durch die Spellcheck-Engine — Viewport-
 * sichtbare zuerst, dann der Rest. Der Worker arbeitet FIFO, deshalb landen
 * die Wellen im sichtbaren Bereich frueh, statt wie zuvor in stricter
 * Modell-Reihenfolge (am Doc-Ende waren die Markierungen erst nach Sekunden
 * da, wenn der Cursor am Ende stand).
 */
function _sweepAllParagraphsViewportFirst() {
    if (!ctx.editor || !ctx.spellCheckManager) return
    const view = ctx.editor.view
    const lines = view && view.lines ? view.lines : []
    const start = view && typeof view.viewportStart === 'number' ? view.viewportStart : 0
    const end = Math.min(start + ((view && view.height) || 0), lines.length)
    const viewportIds = new Set()
    for (let i = start; i < end; i++) {
        const line = lines[i]
        if (line && line.paragraphId) viewportIds.add(line.paragraphId)
    }
    // Cache-Cleanup vor dem Sweep: Paragraphen, die nicht mehr im Modell
    // sind (Cmd+D, Setzungs-Reload, Project-Switch), bleiben sonst in
    // _warnings/_generations/_timers liegen — kleines aber wachsendes
    // Memory-Leak (Spellcheck-Review 2026-05-10 Finding C5). Memory-Editor-
    // Paragraph-IDs werden hier nicht erfasst, sind aber in einem eigenen
    // Pane mit eigenem Lifecycle (open/close → kompletter ProseEditor weg).
    const knownIds = new Set()
    for (const p of ctx.editor.model.paragraphs) knownIds.add(p.id)
    if (ctx.memoryEditor && ctx.memoryEditor.proseEditor) {
        for (const p of ctx.memoryEditor.proseEditor.model.paragraphs) {
            knownIds.add(p.id)
        }
    }
    ctx.spellCheckManager.purgeUnknownParagraphs(knownIds)

    for (const pid of viewportIds) {
        ctx.spellCheckManager.checkNowAllFields(pid)
    }
    for (const p of ctx.editor.model.paragraphs) {
        if (!viewportIds.has(p.id)) {
            ctx.spellCheckManager.checkNowAllFields(p.id)
        }
    }
}

/**
 * Cloud-first fuer den Erst-Start: das beim allerersten Aufruf automatisch
 * angelegte "untitled" still in die Cloud heben — aber NUR, wenn der User
 * nach dem Initial-Sync genau dieses eine Projekt hat. Auf einem neuen
 * Geraet eines Bestandsnutzers bringt der Initial-Sync dessen Cloud-Projekte
 * mit; das lokale Auto-"untitled" bleibt dann lokal, sonst wuerde jedes
 * neue Geraet ein leeres Duplikat auf den Server pushen.
 * (Testphasen-Entscheidung 2026-07-02; Dashboard-Neuanlagen laufen separat
 * ueber ProjectDashboard._autoCloudNewProject.)
 */
async function autoCloudFirstProject(ctx) {
    try {
        const projects = await ctx.projectManager.getAllProjects()
        if (projects.length !== 1) return
        const project = projects[0]
        if (project.cloudEnabled) return
        // Nur ein frisches, praktisch leeres Projekt automatisch heben —
        // ein gewachsenes lokales Projekt bleibt eine bewusste User-Sache.
        if ((project.paragraphOrder?.length || 0) > 1) return
        await enableCloudForNewProject({
            project,
            projectManager: ctx.projectManager,
            syncService: ctx.syncService
        })
    } catch (err) {
        console.warn('Auto-Cloud fuer Erst-Projekt fehlgeschlagen:', err.message)
    }
}

/**
 * Initialisiert die gesamte Anwendung.
 */
async function init() {
    const container = document.getElementById('app')
    if (!container) {
        console.error('Container #app not found!')
        return
    }

    // Renderer erstellen
    ctx.renderer = new Renderer(container)

    // Gespeicherte Settings laden und VOR dem Font-Loading anwenden
    const savedSettings = loadSettings()
    if (savedSettings) {
        await applySettings(savedSettings)
    }

    // Renderer initialisieren (lädt Fonts)
    await ctx.renderer.init()

    // === Auth-Check (vor Editor-Initialisierung) ===
    const authService = new AuthService()
    ctx.authService = authService
    // B4: 'online'-Listener — eine offline gestartete Session prueft den
    // Login erneut, sobald der Browser Netz meldet (sonst blieben alle
    // Sync-Gates bis zum Reload zu).
    authService.watchOnline()
    const authResult = await authService.checkAuth()

    // URL-Parameter pruefen (von Auth-Callback)
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('blocked')) {
        showBlockedPage(ctx.renderer)
        window.history.replaceState({}, '', window.location.pathname)
        return
    }

    if (authResult === 'blocked') {
        showBlockedPage(ctx.renderer)
        return
    }

    if (authResult === null) {
        // Nicht eingeloggt + Server erreichbar → Login-Seite
        const loginPage = new LoginPage(ctx.renderer)
        loginPage.show()
        return  // init() stoppt hier — nach Google-Login kommt Seiten-Reload
    }

    // Eingeloggt (oder Offline mit Cache)
    ctx.user = authResult

    // Account-Wiederherstellung pruefen (nach Google-Redirect)
    if (urlParams.has('restore')) {
        window.history.replaceState({}, '', window.location.pathname)
        // Restore-Dialog wird spaeter vom UserMenu/DialogManager gehandelt
        // Hier nur merken, dass wir im Restore-Flow sind
        ctx._pendingRestore = true
    }

    // Grid-Dimensionen berechnen
    const { cols, rows } = ctx.renderer.calculateGridDimensions()

    // Screen erstellen (wird für Overlay-Dimensionen gebraucht)
    ctx.screen = new Screen(cols, rows)

    // Layout-Manager erstellen
    ctx.layout = new LayoutManager(cols, rows)
    ctx.layout._isProportional = ctx.renderer.isProportional()

    // Layout-Settings anwenden (falls gespeichert)
    applyLayoutSettings(savedSettings)

    // Cursor initial auf aktiven Bereich setzen
    ctx.layout.exitNavigationMode()

    // MetadataManager erstellen (vor Editor, da Titel beim Laden gebraucht wird)
    ctx.metadata = new MetadataManager()

    // ProjectManager erstellen und initialisieren (IndexedDB + ggf. Migration)
    const store = new IndexedDBStore()
    ctx.projectManager = new ProjectManager(store, ctx.user.id)
    const { project, paragraphs } = await ctx.projectManager.initialize()
    ctx.currentProjectId = ctx.projectManager.currentProjectId

    // ProseEditor für Main-Pane erstellen
    const mainPane = ctx.layout.panes[PaneId.MAIN]
    if (mainPane) {
        ctx.editor = new ProseEditor(mainPane.innerBounds)

        // Renderer-Referenz für proportionale Font-Unterstützung
        ctx.editor.view.renderer = ctx.renderer

        // Editor-Dimensionen an das restaurierte Layout anpassen — ohne diesen
        // Sync hat die View default-0-Breiten fuer outline/context, der Filter-
        // Mode bleibt null und info-/note-Zeilen werden leer gebrochen. Nach dem
        // Restore wuerde dann der Cursor auf keiner gueltigen Feldzeile landen.
        ctx.editor.resize(
            effectiveEditorBounds(),
            calcOutlinePaneWidth(),
            calcContextPaneWidth()
        )

        // Projekt-Inhalt laden — als Remote-Apply-Block, damit saveContent()
        // im Render-Loop den frisch geladenen Stand nicht sofort als lokale
        // Aenderung an den Server pusht. applyRemoteChanges() setzt das
        // Model-Flag, ruft setParagraphsRT, baut die View neu auf und setzt
        // Model + View auf clean.
        if (paragraphs && paragraphs.length > 0) {
            ctx.editor.applyRemoteChanges(() => {
                ctx.editor.model.setParagraphsRT(paragraphs)
                // Section Lock fuer ersten Abschnitt wiederherstellen
                ctx.editor.model._firstSectionLocked = !!project.firstSectionLocked
            })
            ctx.editor.view.viewportStart = 0
            ctx.editor.view.markDirty()

            // Cursor aus Projekt-State wiederherstellen
            restoreCursorFromProjectState(project.cursorState)

            // Titel wiederherstellen
            if (project.title) {
                ctx.metadata.setTitle(project.title)
            }
            if (project.author) {
                ctx.metadata.setAuthor(project.author)
            }
        }
    }

    // ProjectManager mit Editor verbinden (Paragraphen-Provider für Flush)
    if (ctx.editor) {
        ctx.projectManager.setEditorParagraphsProvider(() => ctx.editor.model.paragraphs)
    }

    // Spellcheck-Bootstrap (Phase 1: hardcoded DE, Toggle kommt mit PR 2).
    // Manager spawnt seinen eigenen Worker und laedt das Woerterbuch lazy.
    // Bei jedem Paragraph-Dirty-Event triggert der Manager einen Worker-Job
    // (debounced) und legt die Warnings im Cache ab. Beim Update-Callback
    // wird die EditorView fuer den Paragraph neu gerendert, damit die
    // Wavy-Underline erscheint.
    if (ctx.editor) {
        // Pfad-Auswahl: Production lebt unter /app/, Dev direkt unter /.
        const dictBasePath = window.location.pathname.startsWith('/app/')
            ? '/app/dictionaries'
            : '/dictionaries'
        console.debug('[SpellCheck] bootstrapping, basePath=', dictBasePath)
        const spellLoader = new DictionaryLoader(store, undefined, dictBasePath)
        ctx.spellCheckManager = new SpellCheckManager({
            dictionaryLoader: spellLoader,
            // Lookup-Order: Editor-Model zuerst, Memory-Editor als Fallback.
            // UUID-Paragraph-IDs kollidieren nicht, also reicht "wer kennt
            // den Paragraph". Memory-Editor lebt nur waehrend isOpen — sonst
            // ist proseEditor null und der Lookup degradiert sauber zu null.
            getParagraph: (paraId, field) => {
                let para = ctx.editor && ctx.editor.model
                    ? ctx.editor.model.getParagraph(paraId)
                    : null
                if (!para && ctx.memoryEditor && ctx.memoryEditor.proseEditor) {
                    para = ctx.memoryEditor.proseEditor.model.getParagraph(paraId)
                }
                return paragraphToCheckInput(para, field)
            }
        })
        ctx.editor.view.spellCheckManager = ctx.spellCheckManager

        // EditorModel → Manager: jede Paragraph-Mutation triggert einen
        // debouncten Check fuer alle drei Felder (text/note/info). Der
        // Manager filtert leere Felder und Code-Block-Texte selbst raus,
        // ein zusaetzlicher Roundtrip pro Feld ist also gratis. Zusaetzlich
        // Cursor-Snapshot pflegen, damit `_getEffectiveSpellWarnings` weiss,
        // an welcher Stelle gerade tatsaechlich getippt wird (vs. reinem
        // Klick auf ein Warning-Wort).
        ctx.editor.model.addParagraphDirtyListener((paraId) => {
            ctx.spellCheckManager.markDirty(paraId, 'text')
            ctx.spellCheckManager.markDirty(paraId, 'note')
            ctx.spellCheckManager.markDirty(paraId, 'info')
            const cursor = ctx.editor.model.cursor
            if (cursor) {
                ctx.editor.view._lastEditedCursor = {
                    paragraphId: cursor.paragraphId,
                    field:       cursor.field || 'text',
                    offset:      cursor.offset
                }
            }
        })

        // Manager → View: neue Warnings erfordern nur ein Repaint, KEIN Line-
        // Rebuild. Wavy-Underlines sind additiv (Renderer holt die Warnings
        // live im Render-Pfad ueber `_getEffectiveSpellWarnings`), und
        // parse-/positionMap-Caches haengen nur vom Text/Formatten ab.
        // V3.1 (System-Review 2026-06-10): frueher loeste jedes Worker-
        // Ergebnis per markDirty() einen Full-Render aus — beim Initial-
        // Sweep eine ganze Kaskade. Jetzt: offscreen-Absaetze sind No-ops,
        // sichtbare laufen ueber den schmalen Spell-Repaint-Pfad im
        // renderLoop (nur die Absatz-Zeilen, kein _computeSpacing, kein
        // _clampViewport). Memory-/Personal-Dict-Editor haben eigene
        // Modelle/Views — deren kleine Pane-Dokumente rendern wie bisher
        // ueber den Full-Pfad.
        ctx.spellCheckManager.onUpdate((paraId) => {
            if (!ctx.editor) return
            const inMemoryEditor = ctx.memoryEditor && ctx.memoryEditor.isActive()
                && ctx.memoryEditor.proseEditor
                && ctx.memoryEditor.proseEditor.model.getParagraph(paraId)
            const inPersonalDict = ctx.personalDictEditor && ctx.personalDictEditor.isActive()
                && ctx.personalDictEditor.proseEditor
                && ctx.personalDictEditor.proseEditor.model.getParagraph(paraId)
            if (inMemoryEditor || inPersonalDict) {
                ctx.screen.markDirty()
                return
            }
            ctx.editor.view.markSpellWarningsDirty(paraId)
        })

        // Sprache aus Settings: applySettings() hat sie in
        // ctx._pendingSpellcheckLang abgelegt (null = Off, 'de'/'en' = Sprache).
        // Default fuer frische Installs ist 'de'. Fallback (Spec §6.4) ist per
        // Default an; den Wert pflegt der Server (`spellcheck_fallback_enabled`),
        // bis der Bootstrap-Pull unten den finalen Stand setzt.
        // Pro-Projekt-Sprache (Spec §6.2): wenn das aktuelle Projekt einen
        // eigenen Wert hat, gewinnt der; sonst der User-Default aus localStorage.
        if (ctx._spellcheckFallbackEnabled === undefined) {
            ctx._spellcheckFallbackEnabled = true
        }
        const userDefaultLang = ctx._pendingSpellcheckLang === undefined
            ? 'de'
            : ctx._pendingSpellcheckLang
        // Master-Toggle = userDefaultLang !== null. Bei aktivem Master nutzen
        // wir die Pro-Projekt-Sprache (oder fallen auf den User-Default zurueck).
        const initialLang = userDefaultLang === null
            ? null
            : (project && project.spellcheckLang ? project.spellcheckLang : userDefaultLang)
        const sweepAllParagraphs = _sweepAllParagraphsViewportFirst
        // Pro-Projekt-Ignore-Liste schon beim ersten setLanguage anwenden.
        if (project && Array.isArray(project.spellcheckIgnored)) {
            ctx.spellCheckManager.setProjectIgnore(project.spellcheckIgnored)
        }
        if (initialLang !== null) {
            const fallback = _resolveSpellcheckFallback(initialLang, ctx._spellcheckFallbackEnabled)
            ctx.spellCheckManager.setLanguage(initialLang, fallback).then(() => {
                console.debug(`[SpellCheck] setLanguage done (${initialLang}${fallback ? ' + fallback ' + fallback : ''})`)
                sweepAllParagraphs()
            }).catch((err) => {
                console.warn('[Spellcheck] Konnte nicht starten:', err?.message || err)
            })
        } else {
            console.debug('[SpellCheck] disabled by user setting')
        }

        // Async: User-Praeferenzen vom Server holen (Personal-Dict +
        // Master-Toggle). Der Server-Wert hat Vorrang ueber den localStorage-
        // Wert — wenn der User auf einem anderen Geraet abgeschaltet hat,
        // ziehen wir hier nach. Bei Offline / nicht-Login: kein Roundtrip.
        if (ctx.authService && ctx.authService.isLoggedIn && !ctx.authService.isOffline) {
            fetch('/api/user/spellcheck')
                .then(r => r.ok ? r.json() : null)
                .then(prefs => {
                    if (!prefs || !ctx.spellCheckManager) return
                    // Race-Schutz (Spellcheck-Review 2026-05-10 Finding C4):
                    // Wenn der User in den ersten Millisekunden nach dem App-
                    // Start schon ein Wort lokal hinzugefuegt hat (z.B. via
                    // SpellCheckMenu), wuerde ein straight-replace die lokale
                    // Aenderung verwerfen. Wir mergen stattdessen — der
                    // pending PUT von `addPersonal` schreibt den finalen
                    // Stand zurueck.
                    if (Array.isArray(prefs.personal)) {
                        const localBefore = ctx.spellCheckManager.getPersonal()
                        if (localBefore.length === 0) {
                            if (prefs.personal.length > 0) {
                                ctx.spellCheckManager.setPersonal(prefs.personal)
                            }
                        } else {
                            const merged = Array.from(new Set([
                                ...prefs.personal, ...localBefore
                            ])).sort()
                            const same = merged.length === localBefore.length
                                && merged.every((w, i) => w === localBefore[i])
                            if (!same) {
                                ctx.spellCheckManager.setPersonal(merged)
                                // Server mit dem gemergeten Stand re-syncen — sonst
                                // verliert ein Multi-Tab den lokalen Add beim naechsten
                                // Reload.
                                schedulePersonalSync()
                            }
                        }
                    }
                    // Fallback-Setting: Server liefert true/false; merken fuer
                    // spaetere setLanguage-Calls. Default true.
                    const fallbackEnabled = prefs.fallbackEnabled !== false
                    const fallbackChanged = ctx._spellcheckFallbackEnabled !== fallbackEnabled
                    ctx._spellcheckFallbackEnabled = fallbackEnabled

                    const wantLang = prefs.enabled
                        ? (initialLang || 'de')
                        : null
                    const wantFallback = _resolveSpellcheckFallback(wantLang, fallbackEnabled)
                    const langChanged = wantLang !== ctx.spellCheckManager.getLanguage()
                    if (langChanged || fallbackChanged) {
                        ctx.spellCheckManager.setLanguage(wantLang, wantFallback).then(() => {
                            sweepAllParagraphs()
                            if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
                        }).catch(() => {/* nicht eskalieren */})
                    }
                })
                .catch(err => console.warn('[Spellcheck] User-Prefs laden fehlgeschlagen:', err.message))
        }
    }

    // FolderManager — Dashboard Phase 4. Wird auch fuer Anonym-User
    // angelegt (FolderManager arbeitet rein lokal, getUserId liefert
    // einfach null), damit der Code-Pfad symmetrisch ist.
    ctx.folderManager = new FolderManager(
        ctx.projectManager._store,
        () => ctx.user?.id || null
    )

    // SyncService erstellen und mit ProjectManager verbinden (nur wenn eingeloggt)
    if (ctx.user && ctx.authService.isLoggedIn) {
        ctx.syncService = new SyncService(ctx.projectManager._store, ctx.authService)
        ctx.projectManager.setSyncService(ctx.syncService)
        ctx.syncService.setProjectManager(ctx.projectManager)
        // FolderManager ↔ SyncService verbinden (beide Richtungen): Pendings
        // aus dem FolderManager fliessen in den Folder-Sync; Server-Pulls
        // landen umgekehrt im FolderManager.
        ctx.folderManager.setSyncService(ctx.syncService)
        ctx.syncService.setFolderManager(ctx.folderManager)

        // Callback: Server-Aenderungen in aktiven Editor propagieren.
        // Implementierung in js/persistence/remoteChangesHandler.js —
        // extrahiert fuer Testbarkeit (System-Review 2026-06-10, B3:
        // flushNow VOR loadProject, sonst verliert der Remote-Pull die
        // Tipparbeit aus dem 1s-Flush-Fenster).
        ctx.syncService.onRemoteChanges = createRemoteChangesHandler(ctx, saveContent)

        // Pending-State aus vorherigem Session wiederherstellen
        ctx.syncService.restorePendingState()

        // Initial-Sync im Hintergrund, danach periodischen Poll starten
        ctx.syncService.initialSync()
            .then(async () => {
                if (ctx.currentProjectId) {
                    ctx.syncService.startPolling(ctx.currentProjectId)
                }
                // Hat der Sync Cloud-Projekte gebracht (Seed-Projekt bei
                // Erstanmeldung, Bestandsprojekte auf neuem Geraet), waehrend
                // lokal nur das unberuehrte Auto-"untitled" liegt? Dann das
                // Cloud-Projekt oeffnen und das leere untitled entsorgen.
                const replaced = await replaceUntouchedUntitledWithCloudProject({
                    projectManager: ctx.projectManager,
                    tabManager: ctx.tabManager || null,
                    editorModel: ctx.editor?.model || null,
                    currentProjectId: ctx.currentProjectId,
                    switchToProject
                })
                if (!replaced) await autoCloudFirstProject(ctx)
            })
            .catch(err => console.warn('Initial-Sync fehlgeschlagen:', err.message))
    }

    // TabManager erstellen und aus localStorage laden (Key pro User —
    // auf einem geteilten Browser dürfen Tabs eines anderen Accounts
    // nie auftauchen)
    ctx.tabManager = new TabManager(ctx.user.id)
    ctx.tabManager.load()
    // Nur eigene, existierende Projekte als Tabs behalten — räumt
    // fremde und verwaiste (gelöschte) Tab-Einträge weg
    const ownProjects = await ctx.projectManager.getAllProjects()
    ctx.tabManager.retainTabs(ownProjects.map(p => p.id))
    // Falls keine Tabs offen, aktives Projekt als Tab öffnen
    if (ctx.tabManager.openTabs.length === 0 && ctx.currentProjectId) {
        ctx.tabManager.openTab(ctx.currentProjectId)
    }

    // TabBarRenderer erstellen und mit Renderer verbinden
    ctx.tabBarRenderer = new TabBarRenderer()
    ctx.renderer._tabBarRenderer = ctx.tabBarRenderer
    ctx.renderer._tabManager = ctx.tabManager
    ctx.renderer._syncService = ctx.syncService || null
    // Projekt-Titel-Map für Tab-Rendering (wird dynamisch aktualisiert)
    ctx.renderer._projectTitles = new Map()
    _updateProjectTitles()

    // SoundManager erstellen und mit Editor verbinden
    ctx.soundManager = new SoundManager(() => ctx.renderer.getDisplayMode())
    if (ctx.editor) {
        ctx.editor.setActionBlockedCallback(() => ctx.soundManager.playPing())
    }

    // DictationManager erstellen und Callbacks verdrahten
    ctx.dictationManager = new DictationManager({ soundManager: ctx.soundManager })
    _setupDictation()

    // AutoCorrect erstellen und mit Editor/CoreConsole verbinden
    const autoCorrect = new AutoCorrect(() => ctx.renderer.getDisplayMode())
    if (ctx.editor) {
        ctx.editor.input.setAutoCorrect(autoCorrect)
    }

    // CoreConsole fuer Core-Pane erstellen (mit AgentService — Phase 1)
    const corePane = ctx.layout.panes[PaneId.CORE]
    if (corePane) {
        let agentService = null
        try {
            const healthRes = await fetch('/api/health')
            if (healthRes.ok) {
                agentService = new AgentService()
            }
        } catch {
            // Server nicht erreichbar — DummyAgentService greift als Fallback
        }
        // Mock-Transport fuer /mock-Scenarios — nur in Dev-Umgebung aktiv.
        // Produktions-Builds liefern die Scenario-Dateien nicht mit aus
        // (deploy/ schliesst js/agent/scenarios/ aus), zusaetzlich greift
        // der localhost-Guard in ConsoleCommands.isMockEnabled().
        const mockTransport = isMockEnabled() ? new MockAgentTransport() : null
        ctx.mockAgentTransport = mockTransport

        ctx.coreConsole = new CoreConsole(corePane.innerBounds, agentService, ctx.soundManager, mockTransport)
        ctx.coreConsole.view.renderer = ctx.renderer
        ctx.coreConsole.setActionBlockedCallback(() => ctx.soundManager.playPing())
        ctx.coreConsole.input.setAutoCorrect(autoCorrect)
        // ERROR-Zeilen duerfen nicht unsichtbar verhallen: eingeklappte
        // Console automatisch aufklappen — ohne Fokus-Klau, der Cursor
        // bleibt im Editor (Testphasen-Entscheidung 2026-07-02).
        ctx.coreConsole.onErrorEntry = () => {
            ctx.layout?.expandPaneQuietly(PaneId.CORE)
        }
        // Busy-Status der Console an Layout melden — muss hier (direkt nach der
        // Instanziierung) verdrahtet werden, damit der allererste _handleSend
        // (vor _setupDictation) schon das (!) Abort-Control sichtbar macht.
        ctx.coreConsole.setOnBusyChange((busy) => {
            ctx.layout.isAgentBusy = busy
        })
        await ctx.coreConsole.initialize()
        // Initiales Projekt der Console mitteilen — setzt sessionId-Kontext
        // im AgentService und schreibt "Active Project: NAME" in die Console.
        // Beim Erst-Touch verwirft der AgentService zusaetzlich die Server-Session
        // (Reload-Konsistenz).
        if (ctx.currentProjectId) {
            await ctx.coreConsole.switchProject(ctx.currentProjectId, project.title || 'untitled')
        }

        // ReviewController immer initialisieren — sowohl Mock als auch Real-
        // Pfad nutzen ihn fuer das Delta-Review-UI. Im Real-Pfad bekommt er
        // keinen transport (Verdicts werden vom AgentService ueber take-
        // BatchedResults im naechsten Turn mitgereicht). Im Mock-Pfad bekommt
        // er den MockAgentTransport, damit reaktive Scenarios via wait()
        // direkt auf User-Entscheidungen reagieren koennen.
        if (ctx.editor) {
            // Phase 3: Auto-Apply-Pfad braucht zwei Dinge zusaetzlich zum Mock-
            // Setup: (a) `transport` mit reportActionDone() — Real-Pfad nimmt
            // den agentService (er reicht zum RealAgentTransport durch); Mock-
            // Pfad bleibt mockTransport. (b) `flushBeforeAck` — vor
            // POST /action-done erst alle pending Aenderungen in IndexedDB
            // schreiben (ProjectManager.flushNow) UND dann zum Server pushen
            // (SyncService.flushNow), damit der Server-Loop bei Folge-Tools
            // den frischen DB-Stand sieht.
            //
            // Wichtig: ProjectManager.flushNow MUSS vor SyncService.flushNow
            // laufen — sonst kennt syncService die neu erzeugten Paragraphen
            // (z.B. neuer Separator aus create_section) noch nicht in seinem
            // _pendingSyncIds-Set, ruft enqueueParagraphs erst nach dem
            // ProjectManager-Debounce, und der naechste Tool-Call sieht
            // unknown_section. Race aufgedeckt durch E2E-Test 2026-05-01:
            // zwei sequentielle create_section, der zweite mit der UUID des
            // ersten als after_separator_id → unknown_section.
            const autoApplyTransport = agentService || mockTransport
            // Drei Schritte synchron, sonst Race im naechsten Agent-Tool-Call:
            //   1. saveContent() ueberfuehrt EditorModel-Dirty in
            //      ProjectManager-Dirty (passiert sonst nur im Render-Loop —
            //      unser flushBeforeAck laeuft VOR dem naechsten rAF-Tick,
            //      also waere _dirtyParagraphs sonst leer und Schritt 2 ein
            //      No-Op).
            //   2. projectManager.flushNow() schreibt in IndexedDB UND ruft
            //      syncService.enqueueParagraphs() — erst dadurch landen die
            //      neuen IDs ueberhaupt in _pendingSyncIds.
            //   3. syncService.flushNow() pusht zum Server und wartet auf
            //      die HTTP-Response. Erst danach signalisieren wir dem
            //      Server-Loop via reportActionDone, dass die Mutation real
            //      ist — der naechste Tool-Call sieht dann den frischen
            //      DB-Stand.
            // E2E-Bug 2026-05-01: ohne saveContent() blieb der Sync ein
            // No-Op, der zweite create_section-Call lief mit
            // `unknown_section` ins Leere und der Agent dupliziert den Tool-
            // Call statt zu retryen.
            const flushBeforeAck = ctx.syncService
                ? async () => {
                    if (!ctx.currentProjectId) return
                    try { saveContent() }
                    catch (err) { console.warn('[flushBeforeAck] saveContent threw:', err) }
                    if (ctx.projectManager) {
                        try { await ctx.projectManager.flushNow() }
                        catch (err) { console.warn('[flushBeforeAck] projectManager.flushNow threw:', err) }
                    }
                    await ctx.syncService.flushNow(ctx.currentProjectId)
                }
                : null
            ctx.reviewController = new ReviewController({
                model: ctx.editor.model,
                transport: autoApplyTransport,
                flushBeforeAck,
                versionLookup: (paragraphId) => ctx.projectManager.getParagraphVersion(paragraphId),
                panel: ctx.coreConsole,
                viewHint: ctx.editor.view,
                onStateChange: () => {
                    if (ctx.editor) ctx.editor.view.isDirty = true
                    ctx.coreConsole.model._markDirty()
                }
            })
            ctx.coreConsole.setReviewController(ctx.reviewController)

            // Real-Pfad: AgentService bekommt seine Provider gesetzt — dynamisch
            // ausgewerteter Cursor-Context plus Verdict-Sammlung aus dem
            // ReviewController.
            if (agentService) {
                agentService.setCursorContextProvider(() => buildCursorContext({
                    model: ctx.editor.model,
                    layout: ctx.layout
                }))
                agentService.setReviewResultsProvider(() => ctx.reviewController.takeBatchedResults())
            }

            // Mock-Pfad: eigener cursorContext-Provider mit textParagraphs[] fuer
            // Multi-Vorschlag-Scenarios. Sidechannel onRemoteChange ebenfalls
            // verdrahten.
            if (mockTransport) {
                ctx.coreConsole.setCursorContextProvider(() => buildCursorContextForMock(ctx))
                mockTransport.onRemoteChange = (ids) => ctx.reviewController?.onRemoteChange(ids)
            }
        }
    }

    // Cursor erstellen
    ctx.cursor = new Cursor(cols, rows)
    ctx.cursor.startBlinking()  // Kein Callback - Cursor-Blink setzt nur ctx.cursor.blinkDirty

    // Overlay-Stack: zwei Layer fuer echtes Modal-Stacking.
    //   `overlay`     = Base-Layer: Dashboard, Menu, UserMenu, Search,
    //                                LinkPicker, SpellCheckMenu, Tab-Bar,
    //                                (Editor-)ContextMenu.
    //   `overlayTop`  = Top-Layer:  Dialog (immer obenauf) und in Stufe G
    //                                das Dashboard-eigene Context-Menu.
    // Renderer zeichnet beide nacheinander; Mouse-Handler priorisieren
    // Dialog ueber dem Dashboard.
    ctx.overlay = new Overlay(cols, rows)
    ctx.overlayTop = new Overlay(cols, rows)

    // DialogManager rendert auf den Top-Layer, damit er sichtbar ueber dem
    // Dashboard liegt statt es zu ueberdecken.
    ctx.dialogManager = new DialogManager(ctx.overlayTop)

    // Zwei-Tab-Erkennung: Warnung, wenn dasselbe Projekt in einem zweiten
    // Tab dieses Browsers geoeffnet ist (Testphasen-Entscheidung 2026-07-02;
    // divergierende Editor-Staende ueberschreiben sich sonst still beim Flush).
    ctx.tabGuard = createTabGuard({
        getCurrentProjectId: () => ctx.currentProjectId || null,
        onConflict: () => {
            ctx.dialogManager.showInfo(
                'Project open in another tab',
                'This project is open in another browser tab.',
                'Editing in both tabs can overwrite changes — please use one tab.'
            )
        }
    })
    ctx.tabGuard.notifyProjectOpen(ctx.currentProjectId)

    // UserMenu erstellen (fuer Username-Klick in Tab-Leiste)
    if (ctx.authService && ctx.user) {
        ctx.userMenu = new UserMenu(ctx.overlay, ctx.dialogManager, ctx.authService, {
            // Lazy-Lookup: ctx.memoryEditor / ctx.personalDictEditor werden
            // unmittelbar nach UserMenu erstellt — beim Klick auf den jeweiligen
            // Eintrag ist die Referenz da.
            onEditMemory: async () => {
                if (ctx.memoryEditor) {
                    await ctx.memoryEditor.open()
                }
            },
            onEditPersonalDict: async () => {
                if (ctx.personalDictEditor) {
                    await ctx.personalDictEditor.open()
                }
            },
            renderer: ctx.renderer
        })
        // Username in der Tab-Leiste anzeigen
        if (ctx.tabBarRenderer) {
            ctx.tabBarRenderer._username = ctx.user.name || ctx.user.email || ''
        }
    }

    // MemoryEditor erstellen (Spec §6 Phase 4) — der UserMenu-Eintrag in
    // Phase 5 ruft `ctx.memoryEditor.open()` auf. close() stellt den
    // vorherigen aktiven Editor-Pane intern wieder her.
    ctx.memoryEditor = new MemoryEditor({
        layout: ctx.layout,
        dialogManager: ctx.dialogManager,
        renderer: ctx.renderer,
        coreConsole: ctx.coreConsole,
        spellCheckManager: ctx.spellCheckManager
    })

    // PersonalDictEditor (Spellcheck §10 Phase 4) — analog MemoryEditor, aber
    // ohne Locking (kein Multi-Tab-Konflikt: Personal-Dict ist Replace-Semantik
    // und der User hat eh nur einen aktiven Editor offen). Save-on-close.
    ctx.personalDictEditor = new PersonalDictEditor({
        layout: ctx.layout,
        dialogManager: ctx.dialogManager,
        renderer: ctx.renderer,
        spellCheckManager: ctx.spellCheckManager,
        // Bei Aenderungen am Personal-Dict alle Editor-Paragraphen neu pruefen.
        // `setPersonal` allein entfernt nur Warnings fuer jetzt akzeptierte
        // Worte; entfernte Eintraege brauchen einen aktiven Re-Check.
        onAcceptListChanged: () => {
            _sweepAllParagraphsViewportFirst()
            if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
        }
    })

    // ProjectDashboard erstellen (Phase 4 — neue Implementierung)
    ctx.projectDashboard = new ProjectDashboard({
        overlay: ctx.overlay,
        // Top-Layer fuer Modal-Stacking (PathPicker / Stufe G Context-Menu) —
        // damit der Modal-Frame visuell sauber ueber dem Dashboard liegt und
        // keine Stripes/Spans darunter durchscheinen.
        overlayTop: ctx.overlayTop,
        projectManager: ctx.projectManager,
        tabManager: ctx.tabManager,
        dialogManager: ctx.dialogManager,
        folderManager: ctx.folderManager,
        soundManager: ctx.soundManager,
        renderer: ctx.renderer,
        // Cursor-Ref fuer das Blinken des Inline-Edit-Cursors (Stufe C-3).
        cursor: ctx.cursor
    })
    ctx.projectDashboard.setOnOpen(async (projectId) => {
        await switchToProject(projectId)
        _updateProjectTitles()
    })
    if (ctx.syncService) {
        ctx.projectDashboard.setSyncService(ctx.syncService)
        // Cloud-Toggle (Stufe I): Off-Pfad swappt die Projekt-UUID lokal,
        // damit ein parallel laufendes Cloud-Geraet das Projekt nicht
        // ueber denselben Schluessel wieder hochpushen kann (Spec §10.1).
        ctx.projectDashboard.setOnCloudToggle(async (project) => {
            await toggleCloudForProject({
                project,
                projectManager: ctx.projectManager,
                syncService: ctx.syncService,
                tabManager: ctx.tabManager,
                dialogManager: ctx.dialogManager
            })
        })
    }

    // Menu erstellen mit Action-Callbacks
    ctx.menu = new Menu(ctx.overlay, {
        // === Projekt-Dashboard ===
        openDashboard: async () => {
            await ctx.projectDashboard.open()
        },

        // === File-Menü ===
        fileClearContent: async () => {
            const result = await ctx.fileManager.handleClearContent()
            ctx.editor.view.viewportStart = 0
            ctx.layout.isDirty = true
            if (result && ctx.coreConsole) ctx.coreConsole.log('Document content cleared.')
        },
        fileImport: async () => {
            const result = await ctx.fileManager.handleImport()
            ctx.editor.view.viewportStart = 0
            ctx.layout.isDirty = true
            if (result && ctx.coreConsole) {
                const title = ctx.metadata.getTitle()
                ctx.coreConsole.log(title ? `Imported: ${title}` : 'Document imported.')
            }
        },
        fileLoad: async () => {
            const result = await ctx.fileManager.handleLoad()
            ctx.editor.view.viewportStart = 0
            ctx.layout.isDirty = true
            if (result && ctx.coreConsole) {
                const title = ctx.metadata.getTitle()
                ctx.coreConsole.log(title ? `Loaded: ${title}` : 'Document loaded.')
            }
        },
        fileSave: async () => {
            const result = await ctx.fileManager.handleSave()
            if (result && ctx.coreConsole) {
                const title = ctx.metadata.getTitle()
                ctx.coreConsole.log(title ? `Saved: ${title}` : 'Document saved.')
            }
        },
        fileExport: async () => {
            const result = await ctx.fileManager.handleExport()
            if (result && ctx.coreConsole) {
                const title = ctx.metadata.getTitle()
                ctx.coreConsole.log(title ? `Exported: ${title}` : 'Document exported.')
            }
        },

        // Display Mode
        setDisplayMode: async (mode) => {
            const modeConfig = DISPLAY_MODES[mode]
            if (!modeConfig) return

            const needsFontSwitch = modeConfig.font && ctx.renderer.currentFont !== modeConfig.font

            if (needsFontSwitch) {
                // Render-Loop pausieren, bis Fonts UND Grid UND Layout neu sind.
                // Sonst rendert er einen Zwischen-Frame mit neuen Farben aber
                // altem Font/Grid; der spaetere Re-Render mit neuem Grid wischt
                // dann nur die NEUEN innerBounds — alte Pixel an Positionen,
                // die im neuen Grid ausserhalb liegen (anderer charWidth/Height,
                // anderer gridOffset), bleiben als Rand-Reste stehen.
                ctx._modeSwitchInProgress = true
                try {
                    // Fonts ZUERST laden, damit setFont danach synchron umschalten kann.
                    // _waitForFonts ist idempotent — der setFont-interne Aufruf wird
                    // sofort fertig sein (FontFace-Cache).
                    await ctx.renderer._waitForFonts(modeConfig.font)

                    ctx.renderer.setDisplayMode(mode)
                    const dims = await ctx.renderer.setFont(modeConfig.font)

                    // Canvas-Backing einmal sauber leeren (canvas.width = …
                    // setzt den Inhalt auf transparent, HTML5-Canvas-Spec) und
                    // Grid-Offset/Transform fuer die neuen Cell-Dimensionen
                    // neu berechnen. Sonst sind alte Pixel in den Rand-Streifen
                    // weiterhin sichtbar.
                    ctx.renderer._updateCanvasSize()

                    handleDimensionChange(dims)
                    // Cursor-Position invalidieren (pixelX muss nach Font-Wechsel neu berechnet werden)
                    for (const pane of Object.values(ctx.layout.panes)) {
                        pane.cursorPosition = { x: -1, y: -1 }
                    }
                    // Proportional-Flag für Hit-Testing auf Icon-Controls
                    ctx.layout._isProportional = ctx.renderer.isProportional()
                } finally {
                    ctx._modeSwitchInProgress = false
                }
            } else {
                // Kein Font-Wechsel: nur Farben + Mode wechseln, Grid bleibt.
                ctx.renderer.setDisplayMode(mode)
            }

            ctx.screen.markDirty()
            saveSettings()
        },
        getDisplayMode: () => ctx.renderer.getDisplayMode(),

        // Zoom
        zoom: (direction) => {
            handleZoom(direction)
        },

        // Spellcheck (Phase 2 Toggle, Phase 3 erweitert)
        // Off (null) stoppt den Worker; 'de' / 'en' aktivieren die Sprache
        // und sweepen alle Paragraphen einmal durch. Fallback (Spec §6.4)
        // wird automatisch dazugeschaltet, wenn `_spellcheckFallbackEnabled`
        // wahr ist (Server: spellcheck_fallback_enabled).
        setSpellcheckLang: async (lang) => {
            if (!ctx.spellCheckManager) return
            const fallback = _resolveSpellcheckFallback(lang, ctx._spellcheckFallbackEnabled !== false)
            try {
                await ctx.spellCheckManager.setLanguage(lang, fallback)
            } catch (err) {
                console.warn('[Spellcheck] setLanguage failed:', err && err.message)
            }
            if (lang) _sweepAllParagraphsViewportFirst()
            if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
            saveSettings()

            // Pro-Projekt-Sprache mitziehen (nur bei aktiver Sprache).
            // Off-Schaltung laesst die Pro-Projekt-Spalte stehen, damit der
            // User beim Wieder-Aktivieren wieder bei seiner Projekt-Sprache
            // landet.
            if (lang && ctx.projectManager && ctx.currentProjectId) {
                ctx.projectManager.setProjectSpellcheckLang(ctx.currentProjectId, lang)
                    .catch(err => console.warn('[Spellcheck] setProjectSpellcheckLang fehlgeschlagen:', err.message))
            }

            // Master-Toggle zum Server. enabled = lang !== null. Eigener
            // Promise-Zweig, damit ein Netzfehler den UI-Toggle nicht haengen
            // laesst — bei naechstem Reload synct der Bootstrap-Pfad nach.
            if (ctx.authService && ctx.authService.isLoggedIn && !ctx.authService.isOffline) {
                fetch('/api/user/spellcheck', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ enabled: lang !== null })
                }).catch(err => console.warn('[Spellcheck] Toggle-Sync fehlgeschlagen:', err.message))
            }
        },
        getSpellcheckLang: () => ctx.spellCheckManager
            ? ctx.spellCheckManager.getLanguage()
            : null,
        // Fallback-Toggle (Spec §6.4): bei aktiver Sprache `de`/`en` wird
        // gegen die jeweils andere Sprache zusaetzlich geprueft. UI-Eintrag
        // `Spellcheck → Accept English/German words`.
        setSpellcheckFallback: async (enabled) => {
            const next = !!enabled
            if (ctx._spellcheckFallbackEnabled === next) return
            ctx._spellcheckFallbackEnabled = next
            if (ctx.spellCheckManager && ctx.spellCheckManager.getLanguage()) {
                const lang = ctx.spellCheckManager.getLanguage()
                const fallback = _resolveSpellcheckFallback(lang, next)
                try {
                    await ctx.spellCheckManager.setLanguage(lang, fallback)
                } catch (err) {
                    console.warn('[Spellcheck] setLanguage (fallback toggle) failed:', err && err.message)
                }
                _sweepAllParagraphsViewportFirst()
                if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
            }
            if (ctx.authService && ctx.authService.isLoggedIn && !ctx.authService.isOffline) {
                fetch('/api/user/spellcheck', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ fallbackEnabled: next })
                }).catch(err => console.warn('[Spellcheck] Fallback-Sync fehlgeschlagen:', err.message))
            }
        },
        getSpellcheckFallback: () => ctx._spellcheckFallbackEnabled !== false,
        // Dynamisches Label fuer den Fallback-Toggle: zeigt die jeweils
        // ANDERE Sprache an, da das die Fallback-Pruefung verstaerkt.
        // Bei "Off" gibt es keine Fallback-Sprache → generisches Label.
        getSpellcheckFallbackLabel: () => {
            const lang = ctx.spellCheckManager
                ? ctx.spellCheckManager.getLanguage()
                : null
            if (lang === 'de') return 'Fallback English'
            if (lang === 'en') return 'Fallback Deutsch'
            return 'Fallback dictionary'
        },
        // Menue-Toggle: invertiert den Zustand und reicht ihn an die
        // bestehende Set-Action durch. Eigener Eintrag, weil das Menue
        // beim Klick keinen Wert mitgibt.
        toggleSpellcheckFallback: () => {
            const next = !(ctx._spellcheckFallbackEnabled !== false)
            return ctx.menu && ctx.menu.callbacks && ctx.menu.callbacks.setSpellcheckFallback
                ? ctx.menu.callbacks.setSpellcheckFallback(next)
                : null
        },

        // Personal-Dictionary-Editor (Spellcheck §10 Phase 4) — oeffnet einen
        // Full-Screen-Pane, in dem der User die akzeptierten Woerter direkt
        // editieren kann. Save-on-close.
        editPersonalDict: async () => {
            if (ctx.personalDictEditor) {
                await ctx.personalDictEditor.open()
            }
        },

        // Fullscreen
        toggleFullscreen: () => {
            toggleFullscreen()
        },

        // Manual & Reference & About
        // Tab-Recycling: window.open(url, name) recycelt named tabs in
        // modernen Browsern wegen Tab-Isolation/COOP nicht zuverlaessig.
        // Stattdessen halten wir die Window-Reference selbst (ctx._manualWin /
        // ctx._referenceWin), pruefen .closed und navigieren bei lebendem
        // Tab via location.href. Same-origin (writelite.app → writelite.app)
        // erlaubt diesen Zugriff. Falls der User den Tab anderswohin navigiert
        // hat oder das Setzen aus anderem Grund fehlschlaegt, oeffnen wir
        // einen neuen Tab.
        openManual: () => {
            const url = 'https://writelite.app/manual/manual.html'
            ctx._manualWin = _reuseOrOpenTab(ctx._manualWin, url)
        },
        openReference: () => {
            const mode = ctx.renderer ? ctx.renderer.getDisplayMode() : null
            const isNerd = mode === DisplayMode.ULTRA_NERD
                || mode === DisplayMode.REGULAR_NERD
                || mode === DisplayMode.MODERN_NERD
            const url = isNerd
                ? 'https://writelite.app/manual/reference-nerd.html'
                : 'https://writelite.app/manual/reference-writer.html'
            ctx._referenceWin = _reuseOrOpenTab(ctx._referenceWin, url)
        },
        // DSGVO: Datenschutz/Impressum muessen auch aus der laufenden App
        // erreichbar sein, nicht nur von Login-Seite und Website-Footer.
        openLegal: () => {
            const url = 'https://writelite.app/legal/privacy.html'
            ctx._legalWin = _reuseOrOpenTab(ctx._legalWin, url)
        },
        showAbout: () => {
            ctx.menu.showAbout()
        }
    })

    // Dynamische Menü-Items für Project Statistics
    ctx.menu.setDynamicItemsCallback((menuId) => {
        if (menuId === 'projectStatistics') {
            return generateProjectStatisticsMenuItems()
        }
        return null
    })

    // ContextMenu erstellen (teilt sich den Overlay mit Menu/LinkPicker)
    ctx.contextMenu = new ContextMenu(ctx.overlay)

    // SpellCheckMenu erstellen — wird vom Rechtsklick-Routing in
    // mouseHandlers.js und vom Cmd+. Shortcut anstelle des regulaeren
    // ContextMenu geoeffnet, wenn der Cursor auf einem Wort mit Warning
    // steht. Personal-Dict wird Cloud-synchronisiert (PUT /api/user/spellcheck);
    // ignoreInProject ist Phase 3 (Pro-Projekt-Sprache + Ignore-Liste).
    if (ctx.editor && ctx.spellCheckManager) {
        ctx.spellCheckMenu = new SpellCheckMenu(ctx.overlay, {
            editor: ctx.editor,
            spellCheckManager: ctx.spellCheckManager,
            // PUTs serialisiert ueber `_personalSyncChain` — schnelle
            // Doppel-Klicks ("Add to personal dictionary") koennen sonst
            // out-of-order beim Server ankommen, der zweite gewinnt, der
            // erste verliert sein Wort. Siehe Spellcheck-Review 2026-05-10
            // Finding C3b. Body wird beim Senden frisch ausgelesen, damit
            // jeder PUT den aktuellsten Stand schickt.
            onAddPersonal: async (_word) => {
                if (!ctx.authService || !ctx.authService.isLoggedIn || ctx.authService.isOffline) return
                await schedulePersonalSync()
            },
            // Pro-Projekt-Ignore analog: optimistisches Update + serialisierter
            // IndexedDB-Write. Beim Project-Switch wechselt die Chain auf das
            // neue Projekt — pending Writes auf dem alten landen noch dort.
            onIgnoreInProject: async (word) => {
                if (!word || !ctx.spellCheckManager) return
                ctx.spellCheckManager.addProjectIgnore(word)
                if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
                if (ctx.projectManager && ctx.currentProjectId) {
                    await scheduleProjectIgnoreSync(ctx.currentProjectId)
                }
            }
        })
    }

    // SearchManager erstellen (braucht ctx.overlay + ctx.editor.model)
    ctx.searchManager = new SearchManager(ctx.overlay, ctx.editor.model)

    // DialogManager an EditorInput übergeben (für Separator-Bestätigungsdialog)
    ctx.editor.input.setDialogManager(ctx.dialogManager)

    // Main-Pane-Sichtbarkeit für Link-Navigation
    ctx.editor.input.isMainVisible = () => {
        const mainPane = ctx.layout.panes[PaneId.MAIN]
        return mainPane && mainPane.isVisible() && mainPane.innerBounds.width > 0
    }

    // Pane-Aktivierung nach Link-Navigation
    ctx.editor.input.onActivatePane = (field) => {
        const target = field === 'note' ? PaneId.OUTLINE
            : field === 'info' ? PaneId.CONTEXT
            : PaneId.MAIN
        ctx.layout.setActivePane(target)
        ctx.layout.exitNavigationMode()
    }

    // Separator-Insert-Callback im Layout setzen
    ctx.layout.setOnSeparatorInsert(() => {
        if (ctx.editor && ctx.editor.model) {
            // Gesperrter Abschnitt: Ping statt Einfügen
            const cursor = ctx.editor.model.cursor
            if (cursor && ctx.editor.model.isSectionLocked(cursor.paragraphId)) {
                ctx.soundManager.playPing()
                return
            }
            ctx.editor.model.insertSeparator()
        }
    })

    // Background-Insert-Callback im Layout setzen
    ctx.layout.setOnBackgroundInsert(() => {
        if (ctx.editor && ctx.editor.model) {
            ctx.editor.model.insertBackgroundSeparator()
        }
    })

    // Lock-Toggle-Callback im Layout setzen
    // Toggelt den Abschnitt, dessen Label im Pane-Border angezeigt wird
    ctx.layout.setOnLockToggle(() => {
        if (!ctx.editor || !ctx.editor.model) return
        // Welcher Abschnitt wird im Border angezeigt?
        const section = ctx.editor.view.getSectionAboveViewport()
        if (section) {
            // Separator über dem Viewport → dessen Abschnitt toggeln
            ctx.editor.model.toggleSectionLock(section.paragraphId)
        } else if (ctx.editor.model.cursor) {
            // Kein Separator über Viewport → erster Abschnitt (vor erstem Separator)
            ctx.editor.model.toggleSectionLock(ctx.editor.model.paragraphs[0].id)
        }
        ctx.editor.view.markDirty()
        ctx.layout.isDirty = true
        ctx.layout.borderDirty = true
    })

    // Pane-State-Change-Callback: Cursor in Separator-Label mit ins jetzt sichtbare
    // Pane mitnehmen (sonst hängt er auf einer unsichtbaren Trenner-Linie).
    ctx.layout.setOnPaneStateChanged(() => {
        syncSeparatorPaneToLayout()
    })

    // FileManager erstellen (braucht ctx.editor.model, also nach Menu).
    // ProjectManager wird durchgereicht, damit Import/Load den Projekttitel
    // bei untitled-Projekten via renameProject adoptieren koennen. Der
    // UI-Sync nach Rename laeuft zentral ueber projectManager.setOnRenamed
    // unten — damit Pane-Border, Tab-Leiste und Dashboard auch nach einem
    // Dashboard-Rename konsistent bleiben (Boris-Regel 2026-05-12).
    ctx.fileManager = new FileManager(
        ctx.editor.model,
        ctx.metadata,
        // showConfirmDialog
        (title, message, subMessage) => ctx.dialogManager.showConfirm(title, message, subMessage),
        // showInfoDialog
        (title, message, subMessage) => ctx.dialogManager.showInfo(title, message, subMessage),
        { projectManager: ctx.projectManager }
    )

    // Zentraler UI-Sync nach jedem Projekt-Rename: metadata.title-Schatten
    // (fuer Background-Separator-Label etc.), TabBar-Cache und Pane-Border
    // bleiben konsistent — egal ob das Rename aus dem Dashboard oder aus
    // einem File-Import/Load kam.
    ctx.projectManager.setOnRenamed(async (id, newTitle) => {
        if (id === ctx.projectManager.currentProjectId && ctx.metadata) {
            ctx.metadata.setTitle(newTitle || '')
        }
        await _updateProjectTitles()
        ctx.layout.borderDirty = true
    })

    // Event-Listener registrieren
    setupEventListeners()

    // Rendering-Loop starten
    ctx.isRunning = true
    requestAnimationFrame(renderLoop)

    // Account-Wiederherstellungs-Dialog (nach Google-Redirect)
    if (ctx._pendingRestore && ctx.dialogManager) {
        setTimeout(async () => {
            const restore = await ctx.dialogManager.showConfirm(
                'Restore account',
                'Your account is marked for deletion.',
                'Restore it?'
            )
            if (restore) {
                try {
                    await fetch('/api/auth/restore', { method: 'POST' })
                } catch { /* ignorieren */ }
            } else {
                // Nicht wiederherstellen → Logout
                try { await fetch('/api/auth/logout', { method: 'POST' }) } catch {}
                window.location.reload()
            }
            delete ctx._pendingRestore
        }, 500)
    }

    console.log('Carbisdale ready.')
}

// ─── Overlay-Stack Helper ─────────────────────────────────────────
// Beide Layer (Base + Top) symmetrisch behandeln. Pure Helper, kein
// State — operieren direkt auf ctx.overlay / ctx.overlayTop.
function _anyOverlayActive() {
    return ctx.overlay.isActive || (ctx.overlayTop && ctx.overlayTop.isActive)
}
function _anyOverlayDirty() {
    return ctx.overlay.isDirty || (ctx.overlayTop && ctx.overlayTop.isDirty)
}
function _markAllOverlaysClean() {
    ctx.overlay.markClean()
    if (ctx.overlayTop) ctx.overlayTop.markClean()
}
function _consumeOverlayBorderDirty() {
    let dirty = false
    if (ctx.overlay.borderMayBeDirty) { dirty = true; ctx.overlay.borderMayBeDirty = false }
    if (ctx.overlayTop && ctx.overlayTop.borderMayBeDirty) { dirty = true; ctx.overlayTop.borderMayBeDirty = false }
    return dirty
}

/**
 * Die Rendering-Loop.
 */
function renderLoop() {
    if (!ctx.isRunning) return

    // Wenn ein Display-Mode-Wechsel laeuft, NICHT rendern: zwischen
    // setDisplayMode (sync, neue Farben) und setFont (async, neuer Font/Grid)
    // wuerde sonst ein Frame mit neuen Farben aber altem Grid/Font landen,
    // und der spaetere Re-Render mit neuem Grid laesst Reste an den
    // Grid-Raendern stehen (anderer charWidth/Height + anderer gridOffset).
    // Der Callback macht alles atomar und triggert dann genau einen Render.
    if (ctx._modeSwitchInProgress) {
        requestAnimationFrame(renderLoop)
        return
    }

    scrollProfiler.frameStart()

    // Editor-Größe synchronisieren (bei Pane Collapse/Expand)
    syncEditorSize()

    // Editor-Cursor-Position an die Main-Pane synchronisieren
    syncEditorCursor()

    // Memory-Editor: Cursor-Position der Memory-Pane synchronisieren
    syncMemoryEditorCursor()
    syncPersonalDictEditorCursor()

    // Pane-Titel synchronisieren (Separator-Info + Fallbacks)
    syncPaneTitles()

    // Sync-Warn-Glyph am Main-Pane: nur fuer Cloud-Projekte mit gestoertem
    // Sync (offline / Pushes schlagen fehl). setSyncWarning ist bei
    // unveraendertem Wert ein No-Op (2026-07-02).
    syncSyncWarning()

    // CoreConsole synchronisieren
    syncCoreConsoleSize()
    syncCoreConsoleCursor()

    scrollProfiler.mark('sync')

    // Prüfen ob Editor dirty ist und ggf. Layout dirty markieren + Content speichern
    if (ctx.editor && ctx.editor.needsRender()) {
        ctx.layout.isDirty = true
        // Nur speichern wenn Text sich geändert hat (nicht bei reiner Cursor-Bewegung)
        if (ctx.editor.model.getDirtyLevel() !== 'CURSOR_ONLY') {
            saveContent()  // Debounced, speichert nach 1 Sekunde Inaktivität
        }
    }

    // Memory-Editor: Dirty-Check + Live-Save mit 1.5s Debounce (Spec §8)
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor && ctx.memoryEditor.proseEditor.needsRender()
    ) {
        // Full-Render erzwingen — sonst greifen die Cursor-Only-/Paragraph-
        // Partial-Pfade weiter unten, die NUR ctx.editor.model checken und das
        // Overlay-Modell durchrutschen lassen (z.B. Cmd+A → Cmd+X liesse die
        // Worte stehen, bis der naechste Tastendruck den Pfad triggert).
        ctx.screen.markDirty()
        if (ctx.memoryEditor.proseEditor.model.getDirtyLevel() !== 'CURSOR_ONLY') {
            ctx.memoryEditor.scheduleSave()
        }
    }

    // Personal-Dict-Editor: nur Dirty-Check — Save laeuft erst beim close()
    // (Save-on-close, Spellcheck §10 Phase 4).
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor && ctx.personalDictEditor.proseEditor.needsRender()
    ) {
        ctx.screen.markDirty()
    }

    // CoreConsole dirty check.
    // Wenn Core-Pane nicht sichtbar ist (maximiertes anderes Pane), läuft
    // coreConsole.render() — und damit model.clearDirty() — nie. Damit bliebe
    // der Dirty-Flag vom initialen Welcome-Log kleben und triggerte einen
    // Render-Loop. Deshalb in diesem Fall direkt clearen.
    if (ctx.coreConsole && ctx.coreConsole.needsRender()) {
        const corePane = ctx.layout.panes[PaneId.CORE]
        if (corePane && corePane.isVisible() && corePane.innerBounds.width > 0) {
            ctx.layout.isDirty = true
        } else {
            ctx.coreConsole.model.clearDirty()
        }
    }

    // Uhr-Update: jede Minute neu rendern
    const currentMinute = new Date().getMinutes()
    if (currentMinute !== ctx.lastClockMinute) {
        ctx.lastClockMinute = currentMinute
        ctx.layout.isDirty = true
        ctx.layout.borderDirty = true
    }

    // Background-Flag im Layout aktualisieren (steuert Bottom-Border-Control)
    if (ctx.editor && ctx.editor.model) {
        const hasBg = ctx.editor.model.hasBackgroundSeparator()
        ctx.layout.setHasBackground(hasBg)
        // Temporäre Logik: leeren Background entfernen wenn Cursor nicht mehr drin
        if (hasBg && ctx.editor.model.isBackgroundEmpty()) {
            const edCursor = ctx.editor.model.cursor
            if (edCursor && !ctx.editor.model.isInBackground(edCursor.paragraphId)) {
                ctx.editor.model.removeBackgroundSeparator()
            }
        }

        // Section-Lock-Status im Layout aktualisieren (steuert Lock-Control im Main-Border)
        // Zeigt den Status des Abschnitts, dessen Label im Border steht
        const borderSection = ctx.editor.view.getSectionAboveViewport()
        if (borderSection) {
            ctx.layout.isCurrentSectionLocked = ctx.editor.model.isSectionLocked(borderSection.paragraphId)
        } else {
            // Erster Abschnitt (vor erstem Separator) oder Viewport am Anfang
            ctx.layout.isCurrentSectionLocked = ctx.editor.model._firstSectionLocked
        }
    }

    // Link-Picker geschlossen → Overlay muss aufgeräumt werden (Full-Render erzwingen)
    if (ctx.editor && ctx.overlay.isActive && !ctx.editor.input.getLinkPickerState()
        && !ctx.menu.isOpen && !(ctx.contextMenu && ctx.contextMenu.isOpen)
        && !(ctx.dialogManager && ctx.dialogManager.isDialogOpen())
        && !(ctx.searchManager && ctx.searchManager.isSearchOpen())) {
        ctx.overlay.markDirty()
    }

    // Overlay wurde geschlossen → Border-Layer kann von Menü-Resten verschmutzt
    // sein, weil `_clearInnerBounds` pro Frame nur die Pane-Inhalte wischt
    // und Border-Pixel persistent bleiben. Flag wird in overlay.deactivate()
    // gesetzt; hier konsumieren, damit der Full-Render-Pfad den Border neu malt.
    // Beide Layer beruecksichtigen (Base + Top).
    if (_consumeOverlayBorderDirty()) {
        ctx.layout.borderDirty = true
    }

    // Rendern wenn Screen, Layout oder Overlay (Base oder Top) dirty
    const needsRender = ctx.screen.isDirty || ctx.layout.isDirty || _anyOverlayDirty()

    // Scroll-Shift prüfen: Nur scrollen ohne Textänderung → Canvas verschieben statt Full-Render.
    // borderDirty ist OK: nach dem Shift wird `renderBorderOnMain` am Ende des Pfads aufgerufen
    // und zeichnet den Rahmen direkt aufs Hauptcanvas neu (Lesson 54 — kein Offscreen-Cache mehr).
    // Verhindert den "Rupfer" beim Label-Wechsel am Pane-Rand, wenn ein Separator durch die obere
    // Pane-Grenze scrollt.
    const scrollShift = ctx.editor ? ctx.editor.view.getScrollShiftInfo() : { delta: 0, canShift: false }
    const useScrollShift = needsRender && scrollShift.canShift
        && !ctx.screen.isDirty && !_anyOverlayDirty()

    // Einmal pro Frame erzeugen, von allen Render-Pfaden geteilt
    const setCharFn = (x, y, char, style, isSelected = false, highlightType = null) => {
        ctx.renderer._renderCharAtWithSelection(x, y, char, style, isSelected, highlightType)
    }

    if (useScrollShift) {
        // === Scroll-Shift-Pfad: Nur Canvas verschieben + neue Zeilen rendern ===
        const sp = scrollShift.shiftPixels
        // Sub-Pixel-Scroll (Proportional ODER Monospace) braucht Clipping,
        // damit gerenderte Zeilen nicht ausserhalb des Panes überlaufen.
        const needsClippingShift = sp !== 0

        // Profiler-Metadaten vor dem Shift einsammeln (werden hinterher verbraucht)
        const _profView = ctx.editor && ctx.editor.view
        const _profMeta = {
            sp,
            bd: ctx.layout.borderDirty,
            vps: _profView ? _profView.viewportStart : 0,
            vpo: _profView ? (_profView._viewportPixelOffset || 0) : 0,
            xd: scrollShift.exactDelta !== undefined ? scrollShift.exactDelta : sp,
            sec: (() => {
                const s = _profView && _profView.getSectionAboveViewport && _profView.getSectionAboveViewport()
                return s ? (s.number || s.title || '?') : '-'
            })()
        }

        // Scrollbar-Pane bestimmen (wird komplett neu gerendert, nicht mitgeshiftet)
        const scrollbarPane = getScrollbarPane(ctx.layout)

        // Main-Pane shiften und neue Zeilen rendern
        const mainPane = ctx.layout.panes[PaneId.MAIN]
        const mainVisible = mainPane && mainPane.isVisible() && mainPane.innerBounds.width > 0
        if (mainVisible) {
            // Scrollbar-Spalte vom Shift aussparen (wird separat neu gerendert)
            const mainShiftBounds = (scrollbarPane === mainPane)
                ? { ...mainPane.innerBounds, width: mainPane.innerBounds.width - 1 }
                : mainPane.innerBounds
            ctx.renderer.shiftPaneContent(mainShiftBounds, sp)

            ctx.editor.view.searchHighlightFn = ctx.searchManager && ctx.searchManager.isSearchOpen()
                ? (pId, off, field) => ctx.searchManager.getHighlightAt(pId, off, field)
                : null

            if (needsClippingShift) ctx.renderer.setClipRect(mainPane.innerBounds)
            ctx.editor.view.renderPartial(
                mainPane.innerBounds.x,
                mainPane.innerBounds.y,
                scrollShift.delta,
                setCharFn,
                sp
            )
            if (needsClippingShift) ctx.renderer.clearClipRect()
        }
        scrollProfiler.mark('main')

        // Outline-Pane komplett neu rendern (kein Scroll-Shift, da Baumlinien
        // beim Canvas-Shift Lücken bekommen können — Outline ist schmal, Full-Render billig)
        const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
        if (outlinePane && outlinePane.isVisible() && outlinePane.innerBounds.width > 0) {
            // Hintergrund clearen (Shift-Reste vermeiden)
            const oib = outlinePane.innerBounds
            ctx.renderer.ctx.fillStyle = ctx.renderer.colors.background
            ctx.renderer.ctx.fillRect(
                oib.x * ctx.renderer.charWidth, oib.y * ctx.renderer.charHeight,
                oib.width * ctx.renderer.charWidth, oib.height * ctx.renderer.charHeight
            )
            // Outline braucht Clipping auch bei sp === 0 (Sub-Pixel-Frame):
            // drawBatchedConnectors strokt Tree-Linien mit shadowBlur-Glow, der
            // sonst in die Top/Bottom-Border-Zeilen leckt und dort stehen bleibt
            // (Border wird pro Frame nicht gecleart).
            const outlineNeedsClip = needsClippingShift || ctx.renderer.isProportional()
            if (outlineNeedsClip) ctx.renderer.setClipRect(oib)
            ctx.editor.renderOutline(
                oib.x, oib.y,
                calcOutlinePaneWidth(),
                oib.height,
                setCharFn
            )
            if (outlineNeedsClip) ctx.renderer.clearClipRect()
        }
        scrollProfiler.mark('out')

        // Context-Pane shiften und neue Zeilen rendern
        const ctxPane = ctx.layout.panes[PaneId.CONTEXT]
        if (ctxPane && ctxPane.isVisible() && ctxPane.innerBounds.width > 0) {
            const contextShiftBounds = (scrollbarPane === ctxPane)
                ? { ...ctxPane.innerBounds, width: ctxPane.innerBounds.width - 1 }
                : ctxPane.innerBounds
            ctx.renderer.shiftPaneContent(contextShiftBounds, sp)
            if (needsClippingShift) ctx.renderer.setClipRect(ctxPane.innerBounds)
            ctx.editor.renderContextPartial(
                ctxPane.innerBounds.x,
                ctxPane.innerBounds.y,
                calcContextPaneWidth(),
                ctxPane.innerBounds.height,
                scrollShift.delta,
                setCharFn,
                sp
            )
            if (needsClippingShift) ctx.renderer.clearClipRect()
        }
        scrollProfiler.mark('ctx')

        // Scrollbar komplett neu rendern (Spalte wurde nicht mitgeshiftet)
        if (ctx.editor && scrollbarPane && scrollbarPane.isVisible()) {
            const ib = scrollbarPane.innerBounds
            // Scrollbar-Spalte zuerst mit Hintergrundfarbe clearen
            // (░ hat transparente Lücken, durch die alte █-Pixel scheinen würden)
            const sbX = (ib.x + ib.width - 1) * ctx.renderer.charWidth
            const sbY = ib.y * ctx.renderer.charHeight
            ctx.renderer.ctx.fillStyle = ctx.renderer.colors.background
            ctx.renderer.ctx.fillRect(sbX, sbY, ctx.renderer.charWidth, ib.height * ctx.renderer.charHeight)

            ctx.editor.scrollbarViewHeight = ib.height
            ctx.editor.renderScrollbar(
                ib.x + ib.width - 1, ib.y, ib.height,
                setCharFn
            )
        }

        scrollProfiler.mark('sb')

        // Border-Layer aktualisieren falls dirty (z.B. Label-Wechsel beim
        // Separator am Pane-Rand). Direct-Draw ist einziger Pfad (der frühere
        // Offscreen-Cache verursachte GPU-Compositor-Stalls auf Retina-4K).
        if (ctx.layout.borderDirty) {
            ctx.renderer.renderBorderOnMain(ctx.layout)
            ctx.layout.borderDirty = false
        }
        scrollProfiler.mark('bord')

        // Cursor-Layer aktualisieren
        const showCursor = !_anyOverlayActive() || (ctx.contextMenu && ctx.contextMenu.isOpen)
        if (ctx.contextMenu && ctx.contextMenu.isOpen) ctx.cursor.visible = true
        ctx.renderer.renderCursorLayer(ctx.cursor, ctx.layout, showCursor)
        ctx.cursor.blinkDirty = false
        scrollProfiler.mark('curs')

        // Editor-State nach Scroll-Shift-Render aufräumen
        // (markClean resettet _scrollDelta/_scrollPixelDelta; _shiftResidual bleibt
        // bewusst stehen, um den Sub-Pixel-Rest ins nächste Frame zu tragen)
        if (ctx.editor) {
            ctx.editor.model.markClean()
            ctx.editor.view.markClean()
        }

        ctx.screen.markClean()
        ctx.layout.markClean()
        _markAllOverlaysClean()

        scrollProfiler.frameEnd('shift', _profMeta)
    }
    else if (needsRender) {
        // === Cursor-Only-Pfad: Nur Cursor-Layer aktualisieren ===
        let partialDone = false

        if (!ctx.screen.isDirty && !_anyOverlayDirty() && !ctx.layout.borderDirty
            && ctx.editor && ctx.editor.model.getDirtyLevel() === 'CURSOR_ONLY'
            && !ctx.editor.model.selection
            && (ctx.editor.view._scrollDelta || 0) === 0) {
            // Pruefen ob Absatz oder Feld gewechselt hat — beide aendern das
            // Active-Field-Highlight (Marker-Dim) global. Auch das Active-Row-
            // Background im Main folgt dem Absatzwechsel.
            const cursorParaId = ctx.editor.model.cursor ? ctx.editor.model.cursor.paragraphId : null
            const cursorField = ctx.editor.model.cursor ? (ctx.editor.model.cursor.field || 'text') : null
            const outlineView = ctx.editor.outlineView
            const paragraphChanged = outlineView && cursorParaId !== outlineView._lastCurrentParagraphId
            const fieldChanged = outlineView && cursorField !== outlineView._lastCursorField

            if (!paragraphChanged && !fieldChanged) {
                // Kein Content-Render noetig — nur Cursor-Position hat sich geaendert
                const showCursor = !_anyOverlayActive() || (ctx.contextMenu && ctx.contextMenu.isOpen)
                ctx.renderer.renderCursorLayer(ctx.cursor, ctx.layout, showCursor)
                ctx.cursor.blinkDirty = false

                ctx.editor.model.markClean()
                ctx.editor.view.markClean()
                ctx.screen.markClean()
                ctx.layout.markClean()
                _markAllOverlaysClean()
                partialDone = true
                scrollProfiler.frameEnd('cursor-only')
            }
        }

        // === Paragraph-Partial-Pfad versuchen ===
        // Nur dirty-Paragraph-Zeilen rendern (Main + Outline, kein Clear, keine Borders)
        if (!partialDone && !ctx.screen.isDirty && !_anyOverlayDirty() && !ctx.layout.borderDirty
            && ctx.editor && ctx.editor.model.getDirtyLevel() === 'PARAGRAPH'
            && (ctx.editor.view._scrollDelta || 0) === 0) {

            const mainPane = ctx.layout.panes[PaneId.MAIN]
            if (mainPane && mainPane.isVisible() && mainPane.innerBounds.width > 0) {
                ctx.editor.view.searchHighlightFn = ctx.searchManager && ctx.searchManager.isSearchOpen()
                    ? (pId, off, field) => ctx.searchManager.getHighlightAt(pId, off, field)
                    : null

                const clearLineFn = (charX, charY, charWidth, heightQ = 4) => {
                    const lineH = ctx.renderer.isProportional()
                        ? ctx.renderer.charHeightForHeightQ(heightQ)
                        : ctx.renderer.charHeight
                    ctx.renderer.ctx.fillStyle = ctx.renderer.colors.background
                    ctx.renderer.ctx.fillRect(
                        charX * ctx.renderer.charWidth,
                        charY * ctx.renderer.charHeight,
                        charWidth * ctx.renderer.charWidth,
                        lineH
                    )
                }

                // Clipping fuer Quarter-Spacing auch im Partial-Pfad
                const partialNeedsClipping = ctx.renderer.isProportional()

                if (partialNeedsClipping) ctx.renderer.setClipRect(mainPane.innerBounds)
                const dirtyRanges = ctx.editor.tryRenderPartial(
                    mainPane.innerBounds.x, mainPane.innerBounds.y,
                    setCharFn,
                    clearLineFn
                )
                if (partialNeedsClipping) ctx.renderer.clearClipRect()

                if (dirtyRanges) {
                    // Outline-Pane auch partiell rendern (gleiche Zeilen-Indizes)
                    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
                    if (outlinePane && outlinePane.isVisible() && outlinePane.innerBounds.width > 0) {
                        if (partialNeedsClipping) ctx.renderer.setClipRect(outlinePane.innerBounds)
                        ctx.editor.renderOutlineDirtyRanges(
                            outlinePane.innerBounds.x, outlinePane.innerBounds.y,
                            calcOutlinePaneWidth(), outlinePane.innerBounds.height,
                            dirtyRanges,
                            setCharFn,
                            clearLineFn
                        )
                        if (partialNeedsClipping) ctx.renderer.clearClipRect()
                    }

                    // Context-Pane auch partiell rendern (gleiche Zeilen-Indizes)
                    const ctxPanePartial = ctx.layout.panes[PaneId.CONTEXT]
                    if (ctxPanePartial && ctxPanePartial.isVisible() && ctxPanePartial.innerBounds.width > 0) {
                        if (partialNeedsClipping) ctx.renderer.setClipRect(ctxPanePartial.innerBounds)
                        ctx.editor.renderContextDirtyRanges(
                            ctxPanePartial.innerBounds.x, ctxPanePartial.innerBounds.y,
                            calcContextPaneWidth(), ctxPanePartial.innerBounds.height,
                            dirtyRanges,
                            setCharFn,
                            clearLineFn
                        )
                        if (partialNeedsClipping) ctx.renderer.clearClipRect()
                    }

                    // Cursor-Layer aktualisieren
                    const showCursor = !_anyOverlayActive() || (ctx.contextMenu && ctx.contextMenu.isOpen)
                    ctx.renderer.renderCursorLayer(ctx.cursor, ctx.layout, showCursor)
                    ctx.cursor.blinkDirty = false

                    ctx.editor.model.markClean()
                    ctx.editor.view.markClean()
                    ctx.screen.markClean()
                    ctx.layout.markClean()
                    _markAllOverlaysClean()
                    partialDone = true
                    scrollProfiler.frameEnd('partial')
                }
            }
        }

        if (!partialDone) {
            // === Full-Render-Pfad ===

            // Border-Cache invalidieren wenn Layout-Borders sich geändert haben
            if (ctx.layout.borderDirty) {
                ctx.renderer.markBorderDirty()
                ctx.layout.borderDirty = false
            }

            // 1. Haupt-Rendering (Layout, Panes - OHNE Cursor)
            ctx.renderer.render(ctx.screen, ctx.cursor, ctx.overlay.isActive ? ctx.overlay : null, ctx.layout)

            // Clipping für Quarter-Spacing: teilweise sichtbare Zeilen beschneiden
            const needsClipping = ctx.editor && ctx.renderer.isProportional()

            // 2. Editor-Content rendern
            // Zeilen-Rebuild auch wenn Main nicht sichtbar (Outline/Context brauchen aktuelle lines[])
            if (ctx.editor) ctx.editor.view.rebuildIfDirty()

            if (ctx.editor && ctx.layout.panes[PaneId.MAIN]) {
                const mainPane = ctx.layout.panes[PaneId.MAIN]
                if (mainPane.isVisible() && mainPane.innerBounds.width > 0) {
                    // Such-Highlighting setzen wenn Suche offen
                    ctx.editor.view.searchHighlightFn = ctx.searchManager && ctx.searchManager.isSearchOpen()
                        ? (pId, off, field) => ctx.searchManager.getHighlightAt(pId, off, field)
                        : null

                    if (needsClipping) ctx.renderer.setClipRect(mainPane.innerBounds)
                    ctx.editor.render(
                        mainPane.innerBounds.x,
                        mainPane.innerBounds.y,
                        setCharFn
                    )
                    if (needsClipping) ctx.renderer.clearClipRect()
                }
            }

            // 2b. Outline rendern (synchron mit Editor)
            if (ctx.editor && ctx.layout.panes[PaneId.OUTLINE]) {
                const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
                if (outlinePane.isVisible() && outlinePane.innerBounds.width > 0) {
                    const mainPane = ctx.layout.panes[PaneId.MAIN]
                    const mainVisible = mainPane && mainPane.isVisible() && mainPane.innerBounds.width > 0

                    if (needsClipping) ctx.renderer.setClipRect(outlinePane.innerBounds)
                    ctx.editor.renderOutline(
                        outlinePane.innerBounds.x,
                        outlinePane.innerBounds.y,
                        calcOutlinePaneWidth(),
                        outlinePane.innerBounds.height,
                        setCharFn
                    )
                    if (needsClipping) ctx.renderer.clearClipRect()
                }
            }

            // 2c. Context rendern (synchron mit Editor)
            if (ctx.editor && ctx.layout.panes[PaneId.CONTEXT]) {
                const ctxPane = ctx.layout.panes[PaneId.CONTEXT]
                if (ctxPane.isVisible() && ctxPane.innerBounds.width > 0) {
                    if (needsClipping) ctx.renderer.setClipRect(ctxPane.innerBounds)
                    ctx.editor.renderContext(
                        ctxPane.innerBounds.x,
                        ctxPane.innerBounds.y,
                        calcContextPaneWidth(),
                        ctxPane.innerBounds.height,
                        setCharFn
                    )
                    if (needsClipping) ctx.renderer.clearClipRect()
                }
            }

            // 2c2. Memory-Pane rendern (Spec §6 Phase 4)
            if (ctx.memoryEditor && ctx.memoryEditor.isActive()
                && ctx.layout.panes[PaneId.MEMORY]
            ) {
                const memPane = ctx.layout.panes[PaneId.MEMORY]
                if (memPane.isVisible() && memPane.innerBounds.width > 0) {
                    ctx.memoryEditor.syncBounds()
                    if (ctx.memoryEditor.proseEditor) {
                        ctx.memoryEditor.proseEditor.view.rebuildIfDirty()
                    }
                    if (needsClipping) ctx.renderer.setClipRect(memPane.innerBounds)
                    ctx.memoryEditor.render(
                        memPane.innerBounds.x,
                        memPane.innerBounds.y,
                        setCharFn
                    )
                    if (needsClipping) ctx.renderer.clearClipRect()
                }
            }

            // 2c3. Personal-Dict-Pane rendern (Spellcheck §10 Phase 4)
            if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
                && ctx.layout.panes[PaneId.PERSONAL_DICT]
            ) {
                const dictPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
                if (dictPane.isVisible() && dictPane.innerBounds.width > 0) {
                    ctx.personalDictEditor.syncBounds()
                    if (ctx.personalDictEditor.proseEditor) {
                        ctx.personalDictEditor.proseEditor.view.rebuildIfDirty()
                    }
                    if (needsClipping) ctx.renderer.setClipRect(dictPane.innerBounds)
                    ctx.personalDictEditor.render(
                        dictPane.innerBounds.x,
                        dictPane.innerBounds.y,
                        setCharFn
                    )
                    if (needsClipping) ctx.renderer.clearClipRect()
                }
            }

            // 2d. Scrollbar im zuständigen Pane rendern.
            // getActiveScrollSource liefert die richtige ProseEditor-Instanz:
            // im FULLSCREEN-Modus von Memory/PersonalDict deren eigene View
            // (eigener Viewport-State!), sonst den Haupt-Editor.
            {
                const scrollSource = getActiveScrollSource(ctx)
                if (scrollSource && scrollSource.pane.isVisible()) {
                    const ib = scrollSource.pane.innerBounds
                    // Scrollbar-Höhe aktuell halten für Klick/Drag-Berechnung
                    scrollSource.editor.scrollbarViewHeight = ib.height
                    scrollSource.editor.renderScrollbar(
                        ib.x + ib.width - 1, ib.y, ib.height,
                        setCharFn
                    )
                }
            }

            // 2d. CoreConsole im Core-Pane rendern
            if (ctx.coreConsole) {
                const corePane = ctx.layout.panes[PaneId.CORE]
                if (corePane && corePane.isVisible() && corePane.innerBounds.width > 0) {
                    const ib = corePane.innerBounds
                    // Clipping für Quarter-Spacing: teilweise sichtbare Zeilen beschneiden
                    const coreNeedsClipping = ctx.renderer.isProportional()
                    if (coreNeedsClipping) ctx.renderer.setClipRect(ib)
                    ctx.coreConsole.render(ib.x, ib.y, setCharFn)
                    if (coreNeedsClipping) ctx.renderer.clearClipRect()
                    // Core-eigene Scrollbar (außerhalb Clipping)
                    ctx.coreConsole.renderScrollbar(
                        ib.x + ib.width - 1, ib.y, ib.height,
                        setCharFn
                    )
                }
            }

            // 2e. Link-Picker Overlay (wenn aktiv)
            if (ctx.editor) {
                const pickerState = ctx.editor.input.getLinkPickerState()
                if (pickerState) {
                    ctx.overlay.clear()
                    // Picker-Position: unter dem Cursor im aktiven Pane
                    const cursorPos = ctx.editor.getPaneCursorPosition()
                    const field = ctx.editor.model.cursor?.field || 'text'
                    const paneId = field === 'note' ? PaneId.OUTLINE
                        : field === 'info' ? PaneId.CONTEXT : PaneId.MAIN
                    const pane = ctx.layout.panes[paneId]
                    if (cursorPos && pane && pane.isVisible()) {
                        const ib = pane.innerBounds
                        // Proportionale Info durchreichen (wenn proportional).
                        // heightQ=4 wichtig: im Artist-Mode (Garamond) hat der Body-Font einen
                        // System-Font (NotoSans) fuer 4QL-Labels — der Renderer waehlt fuer
                        // den LinkPicker (4QL) den System-Cache via `_activeMetrics(4)`. Wenn
                        // wir hier den Body-Cache (Garamond) durchreichen, misst der Picker
                        // die Breite mit Garamond, rendert dann aber mit Noto → Items ragen
                        // raus. Die heightQ-aware Helper liefern den gleichen Cache wie der
                        // Render-Pfad.
                        const propInfo = ctx.renderer.isProportional() && ctx.renderer._metricsCache
                            ? {
                                metricsCache: ctx.renderer.getGlyphMetricsCache(4),
                                scaleFactor: ctx.renderer.getScaleFactor(4),
                                charWidth: ctx.renderer.charWidth
                            }
                            : null
                        // Anchor wiederverwenden, falls beim ersten Render einer
                        // Open-Session schon gesetzt — sonst springt der Picker
                        // bei jedem Re-Render (Cursor-Blink, Pfeiltaste) durch
                        // Pixel-Rundung in der atCol-Berechnung, und Hover/Klick
                        // sehen Bounds, die nicht zur sichtbaren Position passen.
                        const picker = ctx.editor.input._linkPicker
                        let pickerX, pickerY
                        if (picker && picker.anchor) {
                            pickerX = picker.anchor.x
                            pickerY = picker.anchor.y
                        } else {
                            // Position: @ im Titel alignt mit @ im Text
                            // Titel-Layout: "┌─( @ …" → @ ist bei x+3, also pickerX = atCol - 3
                            // Proportional: pixelX nutzen für exakte Spaltenberechnung
                            let atCol
                            if (cursorPos.pixelX !== undefined) {
                                // pixelX ist relativ zum Pane-Inhalt → in Spalten umrechnen
                                atCol = ib.x + Math.floor((cursorPos.pixelX - ctx.renderer.charWidth) / ctx.renderer.charWidth)
                            } else {
                                atCol = ib.x + cursorPos.x - 1
                            }
                            pickerX = atCol - 3
                            // cursorPos.y ist im Quarter-Spacing-Modus (Prop-Fonts)
                            // fraktional (pixelY / charHeight). Ungefloort wuerde
                            // _bounds.y fraktional — und die Item-Zeilen-Mathematik
                            // im LinkPicker (itemIdx = scrollStart + itemRow) liefert
                            // dann nie einen ganzzahligen Index → Klick/Hover treffen
                            // kein Item. Auf das Char-Grid runden.
                            pickerY = ib.y + Math.floor(cursorPos.y) + 1
                            if (picker) picker.setAnchor(pickerX, pickerY)
                        }
                        const pickerBounds = ctx.overlay.drawLinkPicker(
                            pickerX,
                            pickerY,
                            pickerState.items,
                            pickerState.selectedIndex,
                            pickerState.filter,
                            propInfo,
                            ctx.cursor.visible,  // Blink-Phase fuer Filter-Cursor
                            pickerState.scrollOffset
                        )
                        ctx.editor.input._linkPickerBounds = pickerBounds
                    }
                } else if (ctx.overlay.isActive && !ctx.menu.isOpen
                           && !(ctx.contextMenu && ctx.contextMenu.isOpen)
                           && !(ctx.dialogManager && ctx.dialogManager.isDialogOpen())
                           && !(ctx.searchManager && ctx.searchManager.isSearchOpen())
                           && !(ctx.projectDashboard && ctx.projectDashboard.isOpen)
                           && !(ctx.userMenu && ctx.userMenu.isOpen)
                           && !(ctx.spellCheckMenu && ctx.spellCheckMenu.isOpen)) {
                    ctx.overlay.deactivate()
                }
            }

            // 3. Overlay rendern (Menüs haben höchste Priorität).
            //    Base-Layer zuerst (Dashboard, Menue etc.), dann Top-Layer
            //    (Dialog, Stufe G: Dashboard-Context-Menu) — letzterer liegt
            //    visuell ueber dem Base-Layer.
            // Dashboard-Blinking-Cursor (Inline-Edit oder PathPicker-Filter):
            // vor jedem Full-Render-Overlay-Blit das Overlay neu mit aktuellem
            // cursor.visible bestuecken. Sonst friert der Cursor auf seinem
            // letzten Stand ein, weil der Full-Render-Pfad das Dashboard
            // sonst nicht neu zeichnet.
            if (ctx.projectDashboard && ctx.projectDashboard.hasBlinkingCursor?.()) {
                ctx.projectDashboard.tickCursor()
            }
            if (ctx.overlay.isActive) {
                ctx.renderer.renderOverlay(ctx.overlay)
            }
            if (ctx.overlayTop && ctx.overlayTop.isActive) {
                ctx.renderer.renderOverlay(ctx.overlayTop)
            }

            // 4. Cursor-Layer aktualisieren (nach Content-Render)
            const showCursor = !_anyOverlayActive() || (ctx.contextMenu && ctx.contextMenu.isOpen)
            ctx.renderer.renderCursorLayer(ctx.cursor, ctx.layout, showCursor)
            ctx.cursor.blinkDirty = false

            // Editor-State sauber markieren (sonst bleibt needsRender() true → Endlos-Loop)
            if (ctx.editor) {
                ctx.editor.model.markClean()
                ctx.editor.view.markClean()
            }

            ctx.screen.markClean()
            ctx.layout.markClean()
            _markAllOverlaysClean()
            // Full-Render hat keinen Sub-Profiler-Break (zu viele Sektionen);
            // Boris sieht aber die Gesamtdauer und kann vergleichen mit dem Shift-Pfad.
            const _fullView = ctx.editor && ctx.editor.view
            scrollProfiler.frameEnd('full', {
                bd: false,  // wurde oben schon konsumiert
                vps: _fullView ? _fullView.viewportStart : 0,
                vpo: _fullView ? (_fullView._viewportPixelOffset || 0) : 0,
                // Warum kein Shift? Zeigt ob "echter" Full-Render oder Fallback.
                fullReason: scrollShift.noShiftReason || '-'
            })
        }
    }
    // === Spell-Repaint-Pfad (V3.1, System-Review 2026-06-10) ===
    // Frische Spellcheck-Warnings fuer sichtbare Absaetze, sonst nichts
    // dirty: nur die betroffenen Absatz-Zeilen neu zeichnen (Main + Outline
    // + Context), analog zum Paragraph-Partial-Pfad. Bei aktivem Overlay
    // warten die Repaints — der Full-Render nach Overlay-Close malt die
    // Warnings live und leert die Queue (view.render()).
    else if (ctx.editor && ctx.editor.view.hasPendingSpellRepaint()
        && !_anyOverlayActive()
        && (ctx.editor.view._scrollDelta || 0) === 0) {

        const spellMainPane = ctx.layout.panes[PaneId.MAIN]
        const spellMainVisible = spellMainPane && spellMainPane.isVisible()
            && spellMainPane.innerBounds.width > 0

        if (!spellMainVisible) {
            // Main-hidden-Filtermodi / Fullscreen-Panes: kein Partial-Anker —
            // einmalig den Full-Render-Pfad anstossen (altes Verhalten).
            // Queue VORHER leeren: laeuft der Editor-Render wegen
            // unsichtbarem Main nicht, wuerde die Queue sonst jeden Frame
            // erneut einen Full-Render ausloesen.
            ctx.editor.view.clearSpellRepaints()
            ctx.editor.view.markDirty()
            scrollProfiler.frameEnd('spell')
        } else {
            ctx.editor.view.searchHighlightFn = ctx.searchManager && ctx.searchManager.isSearchOpen()
                ? (pId, off, field) => ctx.searchManager.getHighlightAt(pId, off, field)
                : null

            const spellClearLineFn = (charX, charY, charWidth, heightQ = 4) => {
                const lineH = ctx.renderer.isProportional()
                    ? ctx.renderer.charHeightForHeightQ(heightQ)
                    : ctx.renderer.charHeight
                ctx.renderer.ctx.fillStyle = ctx.renderer.colors.background
                ctx.renderer.ctx.fillRect(
                    charX * ctx.renderer.charWidth,
                    charY * ctx.renderer.charHeight,
                    charWidth * ctx.renderer.charWidth,
                    lineH
                )
            }
            const spellNeedsClipping = ctx.renderer.isProportional()

            if (spellNeedsClipping) ctx.renderer.setClipRect(spellMainPane.innerBounds)
            const spellRanges = ctx.editor.trySpellRepaint(
                spellMainPane.innerBounds.x, spellMainPane.innerBounds.y,
                setCharFn,
                spellClearLineFn
            )
            if (spellNeedsClipping) ctx.renderer.clearClipRect()

            if (spellRanges) {
                // Outline-Pane partiell (Note-Warnings, gleiche Zeilen-Indizes)
                const spellOutlinePane = ctx.layout.panes[PaneId.OUTLINE]
                if (spellOutlinePane && spellOutlinePane.isVisible() && spellOutlinePane.innerBounds.width > 0) {
                    if (spellNeedsClipping) ctx.renderer.setClipRect(spellOutlinePane.innerBounds)
                    ctx.editor.renderOutlineDirtyRanges(
                        spellOutlinePane.innerBounds.x, spellOutlinePane.innerBounds.y,
                        calcOutlinePaneWidth(), spellOutlinePane.innerBounds.height,
                        spellRanges,
                        setCharFn,
                        spellClearLineFn
                    )
                    if (spellNeedsClipping) ctx.renderer.clearClipRect()
                }

                // Context-Pane partiell (Info-Warnings, gleiche Zeilen-Indizes)
                const spellCtxPane = ctx.layout.panes[PaneId.CONTEXT]
                if (spellCtxPane && spellCtxPane.isVisible() && spellCtxPane.innerBounds.width > 0) {
                    if (spellNeedsClipping) ctx.renderer.setClipRect(spellCtxPane.innerBounds)
                    ctx.editor.renderContextDirtyRanges(
                        spellCtxPane.innerBounds.x, spellCtxPane.innerBounds.y,
                        calcContextPaneWidth(), spellCtxPane.innerBounds.height,
                        spellRanges,
                        setCharFn,
                        spellClearLineFn
                    )
                    if (spellNeedsClipping) ctx.renderer.clearClipRect()
                }
            }

            // Cursor-Layer auffrischen (deckt einen gleichzeitigen Blink-Tick ab)
            const showCursor = !_anyOverlayActive() || (ctx.contextMenu && ctx.contextMenu.isOpen)
            ctx.renderer.renderCursorLayer(ctx.cursor, ctx.layout, showCursor)
            ctx.cursor.blinkDirty = false

            scrollProfiler.frameEnd('spell')
        }
    }
    // Nur Cursor-Blink: kein Full Redraw, nur Cursor-Layer updaten
    else if (ctx.cursor.blinkDirty) {
        // LinkPicker-Filter-Cursor lebt im Overlay-Span (nicht im separaten
        // Cursor-Layer) — er kann nicht im Cursor-Only-Pfad blinken. Wenn
        // der Picker offen ist, markieren wir die View dirty, damit das
        // naechste Frame einen Full-Render macht und der Picker den neuen
        // cursor.visible-Wert mitnimmt.
        if (ctx.editor && ctx.editor.input.getLinkPickerState() && ctx.editor.view) {
            ctx.editor.view.markDirty()
        }
        // Dashboard-Blinking-Cursor (Stufe C-3 Inline-Edit + Stufe D
        // PathPicker-Filter): Cursor lebt im Overlay-Span, nicht im
        // Cursor-Layer. tickCursor() bestueckt das Overlay-Buffer neu mit
        // aktuellem cursor.visible; renderOverlay blittet es aufs Canvas,
        // ohne dass ein voller Full-Render noetig wird.
        if (ctx.projectDashboard && ctx.projectDashboard.hasBlinkingCursor?.()) {
            ctx.projectDashboard.tickCursor()
            if (ctx.overlay.isActive) ctx.renderer.renderOverlay(ctx.overlay)
        }
        const showCursor = !_anyOverlayActive() || (ctx.contextMenu && ctx.contextMenu.isOpen)
        if (ctx.contextMenu && ctx.contextMenu.isOpen) ctx.cursor.visible = true
        ctx.renderer.renderCursorLayer(ctx.cursor, ctx.layout, showCursor)
        ctx.cursor.blinkDirty = false
        scrollProfiler.frameEnd('blink')
    } else {
        scrollProfiler.frameEnd('idle')
    }

    // Settings gebündelt speichern (max einmal pro Frame)
    _flushSettings()

    requestAnimationFrame(renderLoop)
}

/**
 * Synchronisiert die Editor-Cursor-Position mit der Main-Pane.
 * Der Framework-Cursor wird an der Position gerendert, die der Editor meldet.
 */
function syncEditorCursor() {
    if (!ctx.editor) return

    // Zeilen-Rebuild VOR Cursor-Positionierung
    // (sonst berechnet dataToScreen pixelX mit staler PositionMap)
    ctx.editor.view.rebuildIfDirty()

    const field = ctx.editor.model.cursor?.field || 'text'

    // Separator-Bearbeitung in anderem Pane erkennen
    const sepState = ctx.editor.input.getSeparatorInputState()
    const isSepOutline = sepState && sepState.pane === 'outline'
    const isSepContext = sepState && sepState.pane === 'context'
    const isSepOtherPane = isSepOutline || isSepContext

    // Main-Pane Cursor
    const mainPane = ctx.layout.panes[PaneId.MAIN]
    if (mainPane && mainPane.isVisible() && mainPane.innerBounds.width > 0) {
        if (field === 'text' && !isSepOtherPane) {
            const cursorPos = ctx.editor.getPaneCursorPosition()
            if (cursorPos) {
                if (mainPane.cursorPosition.x !== cursorPos.x ||
                    mainPane.cursorPosition.y !== cursorPos.y ||
                    mainPane.cursorPosition.pixelX !== cursorPos.pixelX ||
                    mainPane.cursorPosition.heightQ !== cursorPos.heightQ) {
                    mainPane.cursorPosition = { x: cursorPos.x, y: cursorPos.y, pixelX: cursorPos.pixelX, heightQ: cursorPos.heightQ }
                    ctx.layout.isDirty = true
                }
            }
        } else {
            mainPane.cursorPosition = { x: -1, y: -1 }
        }
    }

    // Outline-Pane Cursor (Note-Feld ODER Separator-Bearbeitung im Outline)
    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    if (outlinePane && outlinePane.isVisible() && outlinePane.innerBounds.width > 0) {
        if (field === 'note' || isSepOutline) {
            let notePos
            if (isSepOutline) {
                // Separator im Outline: Cursor-Position im Outline-Titel-Bereich
                const mainPos = ctx.editor.getPaneCursorPosition()
                if (mainPos) {
                    const para = ctx.editor.model.getParagraph(ctx.editor.model.cursor.paragraphId)
                    const depth = para ? para.outlineDepth : 0
                    const number = ctx.editor.view.getSeparatorNumber(ctx.editor.model.cursor.paragraphId) || ''
                    // indent + (-)(3) + ─(1) + number + Space(1) = Titel-Start
                    // (kein Spacer mehr zwischen Marker und Label, analog zum Render-Layout)
                    const labelCol = depth * 2 + 4  // indent + (-)─
                    const titleStartCol = labelCol + (number.length > 0 ? number.length + 1 : 0)
                    const displayCursorPos = sepState.cursorPos - sepState.scrollOffset
                    const displayTitle = para.text.slice(sepState.scrollOffset)
                    // pixelX für proportionalen I-Beam-Cursor
                    const pixelX = ctx.editor.outlineView
                        ? ctx.editor.outlineView.getSeparatorCursorPixelX(displayCursorPos,
                            { separatorDepth: depth, separatorNumber: number }, displayTitle)
                        : undefined
                    notePos = { x: titleStartCol + displayCursorPos, y: mainPos.y, pixelX, heightQ: mainPos.heightQ }
                }
            } else {
                notePos = ctx.editor.getNotePaneCursorPosition()
            }
            if (notePos) {
                if (outlinePane.cursorPosition.x !== notePos.x ||
                    outlinePane.cursorPosition.y !== notePos.y ||
                    outlinePane.cursorPosition.pixelX !== notePos.pixelX ||
                    outlinePane.cursorPosition.heightQ !== notePos.heightQ ||
                    outlinePane.cursorPosition.pixelYOffset !== notePos.pixelYOffset) {
                    outlinePane.cursorPosition = { x: notePos.x, y: notePos.y, pixelX: notePos.pixelX, heightQ: notePos.heightQ, pixelYOffset: notePos.pixelYOffset || 0 }
                    ctx.layout.isDirty = true
                }
            }
        } else {
            outlinePane.cursorPosition = { x: -1, y: -1 }
        }
    }

    // Context-Pane Cursor (Info-Feld ODER Separator-Bearbeitung im Context)
    const contextPane = ctx.layout.panes[PaneId.CONTEXT]
    if (contextPane && contextPane.isVisible() && contextPane.innerBounds.width > 0) {
        if (field === 'info' || isSepContext) {
            let infoPos
            if (isSepContext) {
                // Separator im Context: Cursor-Position wie in Main (── Titel ──)
                const mainPos = ctx.editor.getPaneCursorPosition()
                if (mainPos) {
                    infoPos = { x: mainPos.x, y: mainPos.y, pixelX: mainPos.pixelX, heightQ: mainPos.heightQ }
                }
            } else {
                infoPos = ctx.editor.getInfoPaneCursorPosition()
            }
            if (infoPos) {
                if (contextPane.cursorPosition.x !== infoPos.x ||
                    contextPane.cursorPosition.y !== infoPos.y ||
                    contextPane.cursorPosition.pixelX !== infoPos.pixelX ||
                    contextPane.cursorPosition.heightQ !== infoPos.heightQ ||
                    contextPane.cursorPosition.pixelYOffset !== infoPos.pixelYOffset) {
                    contextPane.cursorPosition = { x: infoPos.x, y: infoPos.y, pixelX: infoPos.pixelX, heightQ: infoPos.heightQ, pixelYOffset: infoPos.pixelYOffset || 0 }
                    ctx.layout.isDirty = true
                }
            }
        } else {
            contextPane.cursorPosition = { x: -1, y: -1 }
        }
    }
}

/**
 * Synchronisiert die Editor-Größe mit der Main-Pane.
 * Wird aufgerufen wenn sich die Pane-Größe durch Collapse/Expand ändert.
 */
function syncEditorSize() {
    if (!ctx.editor || !ctx.layout.panes[PaneId.MAIN]) return

    const bounds = effectiveEditorBounds()
    const outlinePaneWidth = calcOutlinePaneWidth()
    const contextPaneWidth = calcContextPaneWidth()

    // Resizen wenn sich die Dimensionen geändert haben
    // WICHTIG: Auch bei unsichtbarem Main, damit Note/Info-Zeilen
    // korrekt umgebrochen werden (z.B. Outline FULLSCREEN)
    if (ctx.editor.bounds.width !== bounds.width ||
        ctx.editor.bounds.height !== bounds.height ||
        ctx.editor.bounds.x !== bounds.x ||
        ctx.editor.bounds.y !== bounds.y ||
        ctx.editor.view.outlinePaneWidth !== outlinePaneWidth ||
        ctx.editor.view.contextPaneWidth !== contextPaneWidth) {

        ctx.editor.resize(bounds, outlinePaneWidth, contextPaneWidth)

        // View explizit als dirty markieren für korrektes Rendering
        ctx.editor.view.markDirty()
        ctx.layout.isDirty = true
        ctx.layout.borderDirty = true
    }
}

/**
 * Bestimmt das Pane, in dem der Separator-Titel aktuell gerendert wird.
 * Main rendert das Label, sobald es sichtbar ist — sonst übernimmt das jeweilige
 * maximierte Side-Pane (siehe `_renderSeparatorLine` in OutlineView/ContextView,
 * "Titel nur anzeigen wenn Main nicht sichtbar").
 */
function _separatorTargetPane() {
    const main = ctx.layout.panes[PaneId.MAIN]
    if (main && main.isVisible()) return 'main'
    const outline = ctx.layout.panes[PaneId.OUTLINE]
    if (outline && outline.isVisible()) return 'outline'
    const context = ctx.layout.panes[PaneId.CONTEXT]
    if (context && context.isVisible()) return 'context'
    return null
}

/**
 * Hook auf Pane-State-Wechsel (+/- Klicks): Wenn der Cursor in einem Separator-Label
 * sitzt, in das aktuell sichtbare Pane mitnehmen — sonst bleibt der Cursor visuell
 * auf der unsichtbaren Trenner-Linie des vorherigen Panes hängen.
 *
 * Wird vom Layout direkt nach calculateBounds aufgerufen (deterministisch, nicht
 * im Render-Loop reaktiv über bounds-Vergleiche).
 */
function syncSeparatorPaneToLayout() {
    if (!ctx.editor) return
    const sepState = ctx.editor.input.getSeparatorInputState()
    if (!sepState) return
    const targetPane = _separatorTargetPane()
    if (!targetPane || sepState.pane === targetPane) return

    // Editor-Bounds VOR `_activateSeparatorInput` synchronisieren: die Bridge.activate-
    // Methode ruft `setVisibleWidth(view.width - 6)` für Pane='main'. Ohne vorheriges
    // resize() steht `view.width` noch auf dem Fullscreen-Wert (Main war width=0 → view.width=10),
    // das Label würde nach der Migration auf wenige Zeichen abgeschnitten gerendert.
    // Analog Lesson 65 (Shared-State-Bug zwischen Separatoren).
    const bounds = effectiveEditorBounds()
    ctx.editor.resize(bounds, calcOutlinePaneWidth(), calcContextPaneWidth())

    ctx.editor.input._activateSeparatorInput(sepState.cursorPos, targetPane)
    const layoutPaneId = targetPane === 'outline' ? PaneId.OUTLINE
                       : targetPane === 'context' ? PaneId.CONTEXT
                       : PaneId.MAIN
    ctx.layout.setActivePane(layoutPaneId)
    ctx.editor.view.markDirty()
    ctx.layout.isDirty = true
}

/**
 * Synchronisiert die Cursor-Position der Memory-Pane mit dem Memory-ProseEditor.
 * Ohne diesen Sync rendert der Cursor-Layer auf einer veralteten oder leeren
 * Pane-Cursor-Position — Cursor unsichtbar.
 */
function syncMemoryEditorCursor() {
    if (!ctx.memoryEditor || !ctx.memoryEditor.isActive()) return
    const memPane = ctx.layout.panes[PaneId.MEMORY]
    if (!memPane || !memPane.isVisible()) return
    const pe = ctx.memoryEditor.proseEditor
    if (!pe) return
    pe.view.rebuildIfDirty()
    const cursorPos = pe.getPaneCursorPosition()
    if (!cursorPos) {
        memPane.cursorPosition = { x: -1, y: -1 }
        return
    }
    if (memPane.cursorPosition.x !== cursorPos.x
        || memPane.cursorPosition.y !== cursorPos.y
        || memPane.cursorPosition.pixelX !== cursorPos.pixelX
        || memPane.cursorPosition.heightQ !== cursorPos.heightQ
    ) {
        memPane.cursorPosition = {
            x: cursorPos.x,
            y: cursorPos.y,
            pixelX: cursorPos.pixelX,
            heightQ: cursorPos.heightQ
        }
        ctx.layout.isDirty = true
    }
}

/**
 * Cursor-Sync fuer den Personal-Dictionary-Editor (Spellcheck §10 Phase 4),
 * Strukturgleich zu syncMemoryEditorCursor.
 */
function syncPersonalDictEditorCursor() {
    if (!ctx.personalDictEditor || !ctx.personalDictEditor.isActive()) return
    const dictPane = ctx.layout.panes[PaneId.PERSONAL_DICT]
    if (!dictPane || !dictPane.isVisible()) return
    const pe = ctx.personalDictEditor.proseEditor
    if (!pe) return
    pe.view.rebuildIfDirty()
    const cursorPos = pe.getPaneCursorPosition()
    if (!cursorPos) {
        dictPane.cursorPosition = { x: -1, y: -1 }
        return
    }
    if (dictPane.cursorPosition.x !== cursorPos.x
        || dictPane.cursorPosition.y !== cursorPos.y
        || dictPane.cursorPosition.pixelX !== cursorPos.pixelX
        || dictPane.cursorPosition.heightQ !== cursorPos.heightQ
    ) {
        dictPane.cursorPosition = {
            x: cursorPos.x,
            y: cursorPos.y,
            pixelX: cursorPos.pixelX,
            heightQ: cursorPos.heightQ
        }
        ctx.layout.isDirty = true
    }
}

/**
 * Synchronisiert die CoreConsole-Groesse mit dem Core-Pane.
 */
function syncCoreConsoleSize() {
    if (!ctx.coreConsole) return
    const corePane = ctx.layout.panes[PaneId.CORE]
    if (!corePane || !corePane.isVisible() || corePane.innerBounds.width <= 0) return

    ctx.coreConsole.updateBounds(corePane.innerBounds)
}

/**
 * Synchronisiert die CoreConsole-Cursor-Position mit dem Core-Pane.
 */
function syncCoreConsoleCursor() {
    if (!ctx.coreConsole) return
    const corePane = ctx.layout.panes[PaneId.CORE]
    if (!corePane || !corePane.isVisible()) return

    // Console-Cursor bei HOVER und FOCUSED synchronisieren
    if (ctx.layout.isConsoleActive()) {
        const cursorPos = ctx.coreConsole.getCursorPosition()
        if (cursorPos &&
            (corePane.cursorPosition.x !== cursorPos.x ||
             corePane.cursorPosition.y !== cursorPos.y ||
             corePane.cursorPosition.pixelX !== cursorPos.pixelX ||
             corePane.cursorPosition.heightQ !== cursorPos.heightQ)) {
            corePane.cursorPosition = { x: cursorPos.x, y: cursorPos.y, heightQ: cursorPos.heightQ }
            if (cursorPos.pixelX !== undefined) {
                corePane.cursorPosition.pixelX = cursorPos.pixelX
            }
            ctx.layout.isDirty = true
        }
    } else {
        if (corePane.cursorPosition.x !== -1 || corePane.cursorPosition.y !== -1) {
            corePane.cursorPosition = { x: -1, y: -1 }
        }
    }
}

/**
 * Synchronisiert das Sync-Warn-Glyph (!) am Main-Pane mit dem
 * SyncService-Zustand: sichtbar nur, wenn das aktive Projekt
 * cloud-synchronisiert ist UND der Sync gestoert ist (2026-07-02).
 */
function syncSyncWarning() {
    if (!ctx.layout || typeof ctx.layout.setSyncWarning !== 'function') return
    const project = ctx.projectManager ? ctx.projectManager.getCurrentProject?.() : null
    const warn = !!(ctx.syncService
        && project?.cloudEnabled
        && ctx.syncService.hasSyncTrouble())
    ctx.layout.setSyncWarning(warn)
}

/**
 * Synchronisiert die Pane-Titel aller drei Editor-Panes.
 * Gleiche Regeln wie Separator-Anzeige im Dokument:
 * - Titel: Main > Outline > Context (max. einmal)
 * - Nummer: Outline immer, Main nur wenn Outline nicht sichtbar + kein Titel, Context nur wenn einziges Pane + kein Titel
 * Fallback ohne Separator: Outline → "Outline", Main → Dokumenttitel, Context → "Context"
 */
function syncPaneTitles() {
    if (!ctx.editor) return

    const outlinePane = ctx.layout.panes[PaneId.OUTLINE]
    const mainPane = ctx.layout.panes[PaneId.MAIN]
    const contextPane = ctx.layout.panes[PaneId.CONTEXT]

    const outlineVisible = outlinePane && outlinePane.isVisible() && outlinePane.innerBounds.width > 0
    const mainVisible = mainPane && mainPane.isVisible() && mainPane.innerBounds.width > 0
    const contextVisible = contextPane && contextPane.isVisible() && contextPane.innerBounds.width > 0

    const section = ctx.editor.view.getSectionAboveViewport()
    const sepTitle = section ? section.title : ''
    const sepNumber = section ? section.number : ''
    // Pane-Border, Tab-Leiste und Dashboard zeigen denselben Namen: alle
    // lesen direkt aus dem Projekt-Record. metadata.title ist ein
    // In-Memory-Schatten (fuer Export-Header, Background-Separator-Label),
    // der durch switchToProject/_syncProjectTitle synchron gehalten wird.
    const currentProject = ctx.projectManager ? ctx.projectManager.getCurrentProject?.() : null
    const docTitle = currentProject ? currentProject.title : (ctx.metadata ? ctx.metadata.getTitle() : '')

    // --- Outline ---
    if (outlinePane) {
        let newTitle
        if (section) {
            // Nummer immer, Titel nur wenn Main nicht sichtbar
            const showTitle = !mainVisible && sepTitle
            newTitle = showTitle ? (sepNumber ? sepNumber + ' ' + sepTitle : sepTitle) : (sepNumber || 'Outline')
        } else {
            newTitle = 'Outline'
        }
        if (outlinePane.title !== newTitle) {
            outlinePane.title = newTitle
            ctx.layout.isDirty = true
            ctx.layout.borderDirty = true
        }
    }

    // --- Main ---
    if (mainPane) {
        let newTitle
        if (section) {
            // Titel wenn vorhanden, Nummer als Fallback wenn Outline nicht sichtbar
            if (sepTitle) {
                newTitle = sepTitle
            } else if (!outlineVisible) {
                newTitle = sepNumber || ''
            } else {
                newTitle = ''
            }
        } else {
            newTitle = docTitle || 'Untitled'
        }
        if (mainPane.title !== newTitle) {
            mainPane.title = newTitle
            ctx.layout.isDirty = true
            ctx.layout.borderDirty = true
        }
    }

    // --- Context ---
    if (contextPane) {
        let newTitle
        if (section) {
            // In einem Separator-Bereich: leer, außer Context ist einziges Pane
            const isOnlyPane = !outlineVisible && !mainVisible
            if (isOnlyPane) {
                newTitle = sepTitle || sepNumber || ''
            } else {
                newTitle = ''
            }
        } else {
            // Dokumentanfang (vor erstem Separator): "Context"
            newTitle = 'Context'
        }
        if (contextPane.title !== newTitle) {
            contextPane.title = newTitle
            ctx.layout.isDirty = true
            ctx.layout.borderDirty = true
        }
    }
}

/**
 * Initialisiert DictationManager-Callbacks und Layout-Integration.
 */
function _setupDictation() {
    if (!ctx.dictationManager) return

    // Tracking-Var fuer die aktive Diktat-Edit-Gruppe: das Model, auf dem
    // gerade ein beginEditGroup() laeuft. Sorgt dafuer, dass eine ganze
    // Utterance (viele Interims + ein Final) zu EINEM Cmd+Z-Schritt
    // kollabiert. Beim Wechsel des Diktat-Ziels mitten in einer Utterance
    // wird die alte Gruppe sauber geschlossen, bevor die neue startet.
    let _dictationGroupModel = null

    const _beginDictationGroup = (model) => {
        if (!model || typeof model.beginEditGroup !== 'function') return
        if (_dictationGroupModel && _dictationGroupModel !== model) {
            if (typeof _dictationGroupModel.endEditGroup === 'function') {
                _dictationGroupModel.endEditGroup()
            }
            _dictationGroupModel = null
        }
        model.beginEditGroup()  // idempotent
        _dictationGroupModel = model
    }

    const _endDictationGroup = () => {
        if (_dictationGroupModel && typeof _dictationGroupModel.endEditGroup === 'function') {
            _dictationGroupModel.endEditGroup()
        }
        _dictationGroupModel = null
    }

    // Interim-Text: an der Cursor-Position einfuegen und selektieren
    ctx.dictationManager.onInterim = (text) => {
        const target = _getDictationTarget()
        if (!target) return

        if (target.type === 'editor') {
            const ed = target.editor
            _beginDictationGroup(ed.model)
            ed.model.insertText(text)
            const cur = ed.model.cursor
            if (cur) {
                const anchor = new Position(cur.paragraphId, cur.offset - text.length, cur.field)
                const head = new Position(cur.paragraphId, cur.offset, cur.field)
                ed.model.setSelection(anchor, head)
            }
            ed.view.ensureCursorVisible()
        } else {
            _beginDictationGroup(ctx.coreConsole.model)
            ctx.coreConsole.model.insertAtCursor(text)
            const endOffset = ctx.coreConsole.model.inputCursor
            const startOffset = endOffset - text.length
            ctx.coreConsole.model.setInputSelection({ anchor: startOffset, head: endOffset })
        }
        ctx.layout.isDirty = true
    }

    // Finaler Text: committen (Selection wird durch insertText automatisch geloescht)
    ctx.dictationManager.onFinal = (text) => {
        const target = _getDictationTarget()
        if (!target) return

        if (target.type === 'editor') {
            const ed = target.editor
            // Falls onFinal ohne vorheriges onInterim feuert (sehr kurze
            // Utterance), trotzdem Gruppe oeffnen — sonst saehe der User
            // die Insert-Operation gar nicht im Undo (bei Console) bzw.
            // als einzelnen Schritt (im Editor, was OK aber inkonsistent
            // waere).
            _beginDictationGroup(ed.model)
            ed.model.insertText(text)
            ed.model.clearSelection()
            _endDictationGroup()
            ed.view.ensureCursorVisible()
            // saveContent() ist nur fuer den Haupt-Editor gedacht — der
            // Memory-Editor laeuft ueber MemoryPersistence (Auto-Save im
            // renderLoop bei Dirty).
            if (!target.isMemory) saveContent()
        } else {
            _beginDictationGroup(ctx.coreConsole.model)
            ctx.coreConsole.model.insertAtCursor(text)
            ctx.coreConsole.model.clearInputSelection()
            _endDictationGroup()
        }
        ctx.layout.isDirty = true
    }

    // Absatz-/Zeilenumbruch
    ctx.dictationManager.onBreak = () => {
        const target = _getDictationTarget()
        if (!target) return

        if (target.type === 'editor') {
            const ed = target.editor
            const field = ed.model.cursor?.field
            if (field === 'text') {
                // Main bzw. Memory: neuer Absatz
                ed.model.splitParagraph()
            } else {
                // Note/Info: Zeilenumbruch (\n einfuegen)
                ed.model.insertText('\n')
            }
            ed.view.ensureCursorVisible()
        } else {
            // Console: Zeilenumbruch (kein Submit)
            ctx.coreConsole.model.insertAtCursor('\n')
        }
        ctx.layout.isDirty = true
    }

    // State-Aenderung: Layout-Flag und Border aktualisieren
    ctx.dictationManager.onStateChange = (state) => {
        ctx.layout.isDictationStarting = (state === DictationState.STARTING)
        ctx.layout.isDictationActive = (state === DictationState.ACTIVE)
        // Sicherheitsnetz: Bei IDLE (Diktat ist gestoppt) eine evtl. noch
        // offene Edit-Gruppe schliessen. Sonst wuerden Folgeaktionen (User
        // tippt nach Diktat-Stop) NICHT mehr im Undo landen — weil
        // _pushUndo waehrend offener Gruppe ein no-op ist.
        if (state === DictationState.IDLE) {
            _endDictationGroup()
        }
    }

    // Zeichen vor dem Cursor abfragen (fuer Leerzeichen-Logik)
    ctx.dictationManager.onGetCharBeforeCursor = () => {
        const target = _getDictationTarget()
        if (!target) return null

        if (target.type === 'editor') {
            const ed = target.editor
            const cur = ed.model.cursor
            if (!cur) return null
            const para = ed.model.getParagraph(cur.paragraphId)
            if (!para) return null
            const fieldText = cur.field === 'note' ? para.note
                : cur.field === 'info' ? para.info
                : para.text
            if (cur.offset === 0) return ''
            return fieldText[cur.offset - 1] || ''
        } else {
            const offset = ctx.coreConsole.model.inputCursor
            if (offset === 0) return ''
            return ctx.coreConsole.model.inputText[offset - 1] || ''
        }
    }

    // Fehler: in Console loggen
    ctx.dictationManager.onError = (message) => {
        if (ctx.coreConsole) {
            ctx.coreConsole.log(`Dictation: ${message}`, 'ERROR')
        }
    }

    // Layout-Callback fuer (*) Control-Klick
    ctx.layout.setOnDictationToggle(() => {
        if (ctx.dictationManager.isActive) {
            ctx.dictationManager.stop()
        } else {
            ctx.dictationManager.start()
        }
    })

    // Layout-Callback fuer (!) Abort-Control — bricht laufenden Agent-Loop ab
    ctx.layout.setOnAgentAbort(() => {
        if (ctx.coreConsole) ctx.coreConsole._handleAbort()
    })

    // Klick auf das (!)-Sync-Warn-Glyph: erklaeren statt raetseln lassen
    ctx.layout.setOnSyncWarningClick(() => {
        if (!ctx.dialogManager) return
        ctx.dialogManager.showInfo(
            'Cloud sync interrupted',
            'Your changes are saved on this device, but not yet backed up to the cloud.',
            'Sync resumes automatically once the connection returns.'
        )
    })
}

/**
 * Bestimmt das aktuelle Diktat-Ziel.
 * Reihenfolge:
 *   1. Memory-Editor wenn offen (eigener ProseEditor, kein Save ueber saveContent)
 *   2. Console wenn aktiv
 *   3. Haupt-Editor (Default)
 * @returns {{ type: 'editor', editor: object, isMemory?: boolean }
 *          | { type: 'console' }
 *          | null}
 */
function _getDictationTarget() {
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor
    ) {
        return { type: 'editor', editor: ctx.personalDictEditor.proseEditor, isMemory: true }
    }
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor
    ) {
        return { type: 'editor', editor: ctx.memoryEditor.proseEditor, isMemory: true }
    }
    if (ctx.layout.isConsoleActive() && ctx.coreConsole) {
        return { type: 'console' }
    }
    if (ctx.editor) {
        return { type: 'editor', editor: ctx.editor }
    }
    return null
}

/**
 * Wechselt zum angegebenen Projekt.
 * Speichert das aktuelle Projekt, lädt das neue und aktualisiert den Editor.
 * @param {string} projectId - UUID des Zielprojekts
 */
/**
 * Aktualisiert die Projekt-Titel-Map für das Tab-Bar-Rendering.
 */
async function _updateProjectTitles() {
    if (!ctx.projectManager || !ctx.renderer._projectTitles) return
    try {
        const projects = await ctx.projectManager._store.getAllProjects()
        const map = ctx.renderer._projectTitles
        map.clear()
        for (const p of projects) {
            map.set(p.id, { title: p.title || 'untitled' })
        }
    } catch {
        // Fehler ignorieren — Titel-Map ist nur für Rendering
    }
}

export async function switchToProject(projectId) {
    if (!ctx.projectManager || !ctx.editor) return
    if (projectId === ctx.currentProjectId) return

    // Tab-Bar-State sofort umstellen (synchron, vor allen Awaits), damit das
    // Highlight waehrend der nachfolgenden async-Schritte (flush, sync, reset)
    // bereits den neuen Tab zeigt. openTab statt setActiveTab — sonst wird
    // ein vom Dashboard aus erstmals geoeffnetes Projekt zwar zum
    // currentProject, taucht aber nie in der Tab-Bar auf (setActiveTab ist
    // no-op wenn die Projekt-ID noch nicht in openTabs steht). openTab ist
    // idempotent: schon offene Tabs werden nicht doppelt gepusht.
    if (ctx.tabManager) {
        ctx.tabManager.openTab(projectId)
    }

    // Aktuelles Projekt speichern
    await ctx.projectManager.flush(() => ctx.editor.model.paragraphs)
    saveEditorState()

    // Cloud-Projekt: erst Server-Daten holen, dann aus IndexedDB laden
    if (ctx.syncService) {
        const targetProject = await ctx.projectManager.getProject(projectId)
        if (targetProject?.cloudEnabled) {
            try {
                await ctx.syncService.syncProject(projectId)
            } catch (err) {
                console.warn('Sync vor Projektwechsel fehlgeschlagen:', err.message)
            }
        }
    }

    // Neues Projekt laden (jetzt mit aktuellen Server-Daten in IndexedDB)
    const { project, paragraphs } = await ctx.projectManager.loadProject(projectId)
    ctx.currentProjectId = projectId

    // Zwei-Tab-Erkennung: andere Tabs fragen, ob das Projekt dort offen ist
    if (ctx.tabGuard) {
        ctx.tabGuard.notifyProjectOpen(projectId)
    }

    // Poll auf neues Projekt umschalten
    if (ctx.syncService) {
        ctx.syncService.setActiveProject(projectId)
    }

    // EditorInput-State zurücksetzen (Format-Makros, Nav-History etc.)
    ctx.editor.input.resetTransientState()

    // Editor-Inhalt ersetzen (löscht auch Undo/Redo-Stack)
    if (paragraphs && paragraphs.length > 0) {
        ctx.editor.model.setParagraphsRT(paragraphs)
        // Section Lock fuer ersten Abschnitt wiederherstellen
        ctx.editor.model._firstSectionLocked = !!project.firstSectionLocked
        // Dirty-State zuruecksetzen — Daten kommen aus IndexedDB, kein Flush noetig
        ctx.editor.model.markClean()
    } else {
        ctx.editor.model.setText('')
    }
    ctx.editor.view.viewportStart = 0
    // Lines explizit neu bauen — markClean() setzt _dirtyLevel auf CLEAN,
    // aber rebuildIfDirty() braucht STRUCTURAL um den Rebuild auszulösen
    ctx.editor.view._rebuildLines()
    ctx.editor.view.markDirty()

    // Cursor aus Projekt-State wiederherstellen
    restoreCursorFromProjectState(project.cursorState)

    // Metadata aktualisieren
    ctx.metadata.setTitle(project.title || '')
    ctx.metadata.setAuthor(project.author || '')

    // Alle Views dirty markieren
    ctx.layout.isDirty = true
    ctx.layout.borderDirty = true
    ctx.renderer.markBorderDirty()
    ctx.screen.markDirty()

    // Tab-Bar Titel aktualisieren + aktiven Tab sichtbar machen
    _updateProjectTitles()
    if (ctx.tabBarRenderer && ctx.tabManager) {
        ctx.tabBarRenderer.ensureActiveTabVisible(ctx.tabManager)
    }

    // Console-State pro Projekt: Entries, sessionId und busy-Flag umschalten.
    // "Active Project: NAME" wird automatisch geloggt (mit Dedup + Busy-Check).
    // Beim Erst-Touch eines Projekts pro Tab verwirft der AgentService die
    // Server-Session (Reload-Konsistenz).
    if (ctx.coreConsole) {
        await ctx.coreConsole.switchProject(projectId, project.title || 'untitled')
    }

    // Spellcheck auf das neue Projekt umstellen (Spec §6.2 + §7.4):
    //  - Pro-Projekt-Sprache aktivieren, falls gesetzt; sonst User-Default.
    //  - Pro-Projekt-Ignore-Liste in den Manager nachziehen, sonst stehen
    //    noch die Wellen aus dem vorigen Projekt.
    // Wenn der Master-Toggle aus ist (manager.getLanguage() === null), ist
    // hier nichts zu tun — der Manager bleibt off.
    if (ctx.spellCheckManager) {
        const currentLang = ctx.spellCheckManager.getLanguage()
        // Project-Ignore IMMER auf den aktuellen Stand bringen, auch wenn
        // Master off ist — sonst zeigt der naechste setLanguage-Sweep die
        // alte Liste.
        ctx.spellCheckManager.setProjectIgnore(
            Array.isArray(project.spellcheckIgnored) ? project.spellcheckIgnored : []
        )
        if (currentLang !== null) {
            const userDefault = (ctx._pendingSpellcheckLang === undefined
                || ctx._pendingSpellcheckLang === null)
                ? 'de'
                : ctx._pendingSpellcheckLang
            const projectLang = project.spellcheckLang || userDefault
            const fallback = _resolveSpellcheckFallback(
                projectLang,
                ctx._spellcheckFallbackEnabled !== false
            )
            if (projectLang !== currentLang
                    || fallback !== ctx.spellCheckManager._fallbackLang) {
                ctx.spellCheckManager.setLanguage(projectLang, fallback).then(() => {
                    _sweepAllParagraphsViewportFirst()
                    if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
                }).catch(err => console.warn('[Spellcheck] Project-Switch setLanguage failed:', err && err.message))
            } else {
                // Sprache identisch → nur sweepen, damit die Project-Ignore-
                // Liste auf bestehende Paragraphen wirkt.
                _sweepAllParagraphsViewportFirst()
                if (ctx.editor && ctx.editor.view) ctx.editor.view.markDirty()
            }
        }
    }
}

/**
 * Registriert alle Event-Listener.
 */
function setupEventListeners() {
    window.addEventListener('resize', handleResize)

    // Phase 6.0c: iOS / iPadOS aendert `window.innerHeight` nicht bei
    // eingeblendeter Soft-Keyboard, sondern verkleinert nur `visualViewport`.
    // Damit unser Canvas-Grid auf die reduzierte Hoehe reagiert, hoeren wir
    // hier zusaetzlich auf `visualViewport.resize` und feuern denselben
    // handleResize-Pfad an.
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize)
    }

    // iPad-PWA-Cold-Start im Landscape: iOS rendert die Page initial mit
    // Portrait-Maessen, korrigiert erst nach `orientationchange`. Ohne diesen
    // Listener bleibt das Canvas-Grid in Portrait-Dimensionen, bis der User
    // zweimal dreht. `orientationchange` deckt das Cold-Start-Problem ab,
    // `load` ist die zweite Sicherung (feuert nach dem finalen Layout-Pass,
    // wenn DOMContentLoaded zu frueh war).
    window.addEventListener('orientationchange', handleResize)
    window.addEventListener('load', handleResize)

    // Keydown in Capture-Phase für früheres Abfangen der Browser-Zoom-Shortcuts
    window.addEventListener('keydown', handleKeydown, true)

    // Keyup für Doppel-Ctrl-Erkennung (Diktat)
    window.addEventListener('keyup', handleKeyup, true)

    // Fullscreen-Change Event
    document.addEventListener('fullscreenchange', handleFullscreenChange)

    // Debug-Hook: aus der Browser-Konsole aufrufbar, um den kompletten
    // Sync-Pending-Zustand (RAM + localStorage) zu clearen. Wird gebraucht,
    // wenn sich stale Pendings aufgestaut haben und der naechste Sync
    // deshalb ins Zod-Limit laeuft. Beispiel: __clearSyncPending()
    window.__clearSyncPending = () => {
        if (!ctx.syncService) {
            console.warn('[debug] SyncService nicht initialisiert')
            return
        }
        ctx.syncService._pendingSyncIds.clear()
        ctx.syncService._pendingDeleteIds.clear()
        ctx.syncService._pendingReorder.clear()
        for (const [, t] of ctx.syncService._syncTimeouts) clearTimeout(t)
        ctx.syncService._syncTimeouts.clear()
        localStorage.removeItem('carbisdale_sync_pending')
        console.log('[debug] Sync pending state cleared (RAM + localStorage)')
    }

    // Vor dem Schliessen: Content sofort speichern, Diktat stoppen, AgentService aufraeumen
    window.addEventListener('beforeunload', () => {
        saveContentImmediately()
        _flushSettings()
        if (ctx.syncService) ctx.syncService.savePendingState()
        if (ctx.dictationManager) ctx.dictationManager.stop()
        if (ctx.coreConsole && ctx.coreConsole.agentService && ctx.coreConsole.agentService.destroy) {
            ctx.coreConsole.agentService.destroy()
        }
    })

    // Maus-Events auf Canvas
    const canvas = ctx.renderer.canvas

    // Mouse-Down für Drag-Initialisierung (kommt VOR click)
    canvas.addEventListener('mousedown', handleMouseDown)

    // Klick auf Bereiche und Controls (kommt NACH mouseup)
    canvas.addEventListener('click', handleClick)

    // Hover für Controls
    canvas.addEventListener('mousemove', handleMouseMove)

    // Sicherheitsnetz: Beim Wiedereintreten in den Canvas Cursor zwingend
    // einblenden — fängt Faelle ab, in denen mousemove kurz nach dem
    // letzten Keydown noch nicht gefeuert hat oder ein Browser-Quirk das
    // Re-Show im selben Event geschluckt hat.
    canvas.addEventListener('mouseenter', () => {
        if (ctx.renderer.canvas.style.cursor === 'none') {
            ctx.renderer.canvas.style.cursor = 'auto'
        }
    })

    // Mouse-Up für Drag-Ende (auf window, damit auch außerhalb des Canvas erkannt wird)
    window.addEventListener('mouseup', handleMouseUp)

    // Mouse-Wheel für Scrollen
    canvas.addEventListener('wheel', handleMouseWheel, { passive: false })

    // Rechtsklick für Menü
    canvas.addEventListener('contextmenu', handleContextMenu)

    // Touch-Events für iPad/Mobile: Scroll-Swipe in Panes, Tap = Klick.
    // passive:false ist Pflicht, damit preventDefault Browser-Defaults
    // (Page-Scroll, Double-Tap-Zoom) unterdrücken darf.
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false })
    canvas.addEventListener('touchcancel', handleTouchCancel, { passive: false })

    // Phase 6.0a: verstecktes <textarea> fuer System-Tastatur-Eingaben auf
    // iPad / iPhone. handleTouchEnd fokussiert es bei Tap in einen Text-Bereich.
    // Auf Desktop schadet es nicht (opacity:0, pointer-events:none, 1×1 px).
    initTouchKeyboardSink()

    // Phase 6.0b: beforeinput-Pfad — die System-Tastatur schickt Eingaben als
    // beforeinput-Events an den Sink, wir bauen daraus synthetische KeyboardEvents
    // und jagen sie durch unsere normale handleKeydown-Pipeline. Damit gilt das
    // gesamte Pane-/Field-/Console-Routing kostenlos auch fuer Touch-Eingaben.
    installBeforeInputHandler(eventInit => {
        const synthEvent = new KeyboardEvent('keydown', {
            ...eventInit,
            bubbles:    true,
            cancelable: true
        })
        handleKeydown(synthEvent)
    })

    // Safety-Net gegen Safari-Pinch-Zoom, falls touch-action/viewport nicht
    // greifen (z.B. außerhalb des Canvas oder in Edge-Cases).
    document.addEventListener('gesturestart', e => e.preventDefault())
    document.addEventListener('gesturechange', e => e.preventDefault())
    document.addEventListener('gestureend', e => e.preventDefault())
}


// === Start ===
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
} else {
    init()
}
